import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import api from '../../api/api';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchProducts, fetchSuppliers } from '../../store/slices/productSlice';
import { InventoryReceipt, InventoryReceiptItem, ImportedReceiptItem } from '../../types';
import ItemQuantityField from '../../components/ItemQuantityField';
import SearchSelect from '../../components/SearchSelect';
import InventoryWarehouseSelect from '../../components/InventoryWarehouseSelect';
import type { WarehouseTransferAvailabilityData } from '../../components/WarehouseTransferAvailability';
import TablePreferencesPanel from '../../components/TablePreferencesPanel';
import { TablePreferenceColumn, useTablePreferences } from '../../hooks/useTablePreferences';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getSelectableProductSearchItems } from '../../utils/selectableProducts';
import { useSupplierDataScope } from '../../hooks/useSupplierDataScope';
import { shouldPrintReceiptPricesByDefault } from '../../utils/inventoryReceiptPrintDefaults';
import { openAndPrintHtml } from '../../utils/printDocument';
import {
  evaluateCalcInput,
  formatCalcValue,
  getResolvedQuantity,
  getResolvedUnitPrice,
  getRowComputedTotal,
  hasPackageMode,
  ItemRow,
  RowUpdateSource,
  syncItemRow,
} from '../../utils/inventoryReceiptCalculations';

const WEIGHT_TYPES = [
  { value: 'kg', label: 'кг' },
  { value: 'g', label: 'г' },
  { value: 'l', label: 'л' },
  { value: 'ml', label: 'мл' },
  { value: 'pcs', label: 'шт' },
];

const getWeightTypeLabel = (weightType: string) => {
  const normalized = (weightType || '').trim().toLowerCase();
  if (normalized === 'kg' || normalized === 'кг') return 'кг';
  if (normalized === 'g' || normalized === 'г') return 'г';
  if (normalized === 'l' || normalized === 'л') return 'л';
  if (normalized === 'ml' || normalized === 'мл') return 'мл';
  if (normalized === 'pcs' || normalized === 'шт' || normalized === 'шт.') return 'шт';
  return weightType || '—';
};

const ReceiptDetails = ({ title, initiallyOpen, children }: {
  title: string;
  initiallyOpen: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(initiallyOpen);
  return (
    <details className="inventory-receipts-entry-details" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>{title}</summary>
      {children}
    </details>
  );
};

const formatUnitPriceDisplay = (value: string) => {
  const parsed = evaluateCalcInput(value);
  return parsed === null ? value : formatCalcValue(parsed, 2);
};

const ReceiptUnitPriceInput = ({
  value,
  disabled,
  onChange,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onCommit: (value: string) => void;
}) => {
  const [draft, setDraft] = useState(() => formatUnitPriceDisplay(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setDraft(formatUnitPriceDisplay(value));
  }, [value]);

  return (
    <input
      aria-label="Ціна за одиницю"
      type="text"
      inputMode="decimal"
      value={draft}
      disabled={disabled}
      onFocus={() => {
        focused.current = true;
        setDraft(formatUnitPriceDisplay(value));
      }}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange(event.target.value);
      }}
      onBlur={(event) => {
        focused.current = false;
        const parsed = evaluateCalcInput(event.currentTarget.value);
        if (parsed !== null) {
          const rounded = formatCalcValue(parsed, 2);
          setDraft(rounded);
          onCommit(rounded);
        } else {
          setDraft(formatUnitPriceDisplay(value));
        }
      }}
    />
  );
};

const emptyRow: ItemRow = {
  productId: '',
  productQuery: '',
  quantity: '',
  unitPrice: '',
  lineTotal: '',
  weightType: 'kg',
  packageCount: '',
  packageWeight: '',
  packagePrice: '',
  lotNumber: '',
  manufactureDate: '',
  expirationDate: '',
};

let nextReceiptEntryKey = 0;
const createReceiptEntryKey = () => `receipt-entry-${++nextReceiptEntryKey}`;

const calculateReceiptVatAmount = (netAmount: number, supplierVatRate: number) => {
  if (!supplierVatRate || supplierVatRate <= 0 || !netAmount || netAmount <= 0) {
    return 0;
  }

  return netAmount * supplierVatRate / 100;
};

const formatLocalDateInput = (value: Date | string) => {
  const date = typeof value === 'string' ? new Date(value) : value;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeDateForApi = (dateOnly: string) => {
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (!year || !month || !day) return dateOnly;
  return new Date(year, month - 1, day, 12, 0, 0).toISOString();
};

const addCalendarDays = (dateOnly: string, days: number) => {
  const [year, month, day] = dateOnly.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  date.setDate(date.getDate() + days);
  return formatLocalDateInput(date);
};

type ReceiptForm = { date: string; paymentDueDate: string; supplierId: string; invoiceNumber: string; notes: string; isPaid: boolean; warehouseId: string; items: ItemRow[] };
const emptyForm = (): ReceiptForm => ({
  date: formatLocalDateInput(new Date()),
  paymentDueDate: formatLocalDateInput(new Date()),
  supplierId: '', invoiceNumber: '', notes: '', isPaid: false, warehouseId: '', items: [{ ...emptyRow, entryKey: createReceiptEntryKey() }],
});

const calendarMonthRange = () => {
  const now = new Date();
  return {
    from: formatLocalDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: formatLocalDateInput(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

type ReceiptSortColumn = 'date' | 'supplier' | 'invoice' | 'status' | 'amount';
type ReceiptSortDirection = 'asc' | 'desc';
type ReceiptStatusFilter = 'all' | 'Draft' | 'Posted' | 'Cancelled' | 'Paid' | 'Unpaid';

const getReceiptStatus = (receipt: InventoryReceipt): 'Draft' | 'Posted' | 'Cancelled' => {
  if (receipt.status === 'Draft' || receipt.status === 'Cancelled') return receipt.status;
  return 'Posted';
};

const receiptStatusPresentation = {
  Draft: { label: 'Чернетка', className: 'inventory-receipt-status--draft' },
  Posted: { label: 'Проведена', className: 'inventory-receipt-status--posted' },
  Cancelled: { label: 'Скасована', className: 'inventory-receipt-status--cancelled' },
};

type ReceiptOcrRow = {
  lineNumber: number;
  sourceText: string;
  sourceProductName: string;
  productId: number | null;
  productName: string;
  matchConfidence: number;
  matchMethod: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  weightType: string;
  warning: string;
  excluded?: boolean;
};

type ReceiptOcrPreview = {
  date: string;
  supplierId: number | null;
  supplierName: string;
  supplierConfidence: number;
  invoiceNumber: string;
  isPaid: boolean;
  rows: ReceiptOcrRow[];
  unparsedLines: string[];
  sourceText?: string;
};

type InventoryReceiptBatchRow = {
  rowNumber: number;
  sourceProductName: string;
  sourceProductCode: string;
  productId: number | null;
  productName: string;
  productSource: string;
  productConfidence: number;
  productMatchMethod: string;
  quantity: number;
  packageCount: number | null;
  packageWeight: number | null;
  packagePrice: number | null;
  weightType: string;
  unitPrice: number;
  lineTotal: number;
  errors: string[];
  warnings: string[];
  excluded?: boolean;
};

type InventoryReceiptBatchReceipt = {
  groupKey: string;
  sourceRowNumber: number;
  date: string;
  supplierId: number | null;
  supplierName: string;
  supplierSource: string;
  supplierConfidence: number;
  supplierMatchMethod: string;
  invoiceNumber: string;
  isPaid: boolean;
  notes: string;
  totalAmount: number;
  rows: InventoryReceiptBatchRow[];
  errors: string[];
  warnings: string[];
  excluded?: boolean;
};

type InventoryReceiptBatchPreviewResult = {
  receipts: InventoryReceiptBatchReceipt[];
  fileWarnings: string[];
};

type ReceiptTableColumnKey = 'date' | 'supplier' | 'invoice' | 'status' | 'amount';
const RECEIPT_TABLE_COLUMNS: readonly TablePreferenceColumn<ReceiptTableColumnKey>[] = [
  { key: 'date', label: 'Дата', defaultWidth: 16, minWidth: 10, maxWidth: 30 },
  { key: 'supplier', label: 'Постачальник', defaultWidth: 27, minWidth: 12, maxWidth: 45 },
  { key: 'invoice', label: '№ накладної', defaultWidth: 15, minWidth: 10, maxWidth: 30, locked: true },
  { key: 'status', label: 'Статус / оплата', defaultWidth: 27, minWidth: 15, maxWidth: 40 },
  { key: 'amount', label: 'Сума', defaultWidth: 15, minWidth: 10, maxWidth: 30 },
];
const RECEIPT_TABLE_PREFERENCES_KEY = 'crm4chef.inventory-receipts.table-preferences.v1';

const thStyle: React.CSSProperties = { padding: '8px', textAlign: 'left', background: 'var(--crm-navbar-dropdown-bg)', color: 'var(--crm-primary-contrast)', borderBottom: '2px solid var(--crm-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--crm-border)', color: 'var(--crm-table-text)' };
type ReceiptButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted' | 'primary' | 'accent';
const btn = (tone: ReceiptButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: `var(--crm-${tone === 'muted' ? 'surface-alt' : tone === 'accent' ? 'accent' : tone === 'primary' ? 'primary' : `${tone}-bg`})`,
  color: `var(--crm-${tone === 'muted' ? 'text' : tone === 'primary' ? 'primary-contrast' : tone === 'accent' ? 'accent-contrast' : tone})`,
  border: `1px solid var(--crm-${tone === 'muted' ? 'border' : tone === 'primary' ? 'primary' : tone === 'accent' ? 'accent' : tone})`,
  borderRadius: 'var(--crm-radius-sm)',
  cursor: 'pointer',
  padding: '8px 16px',
  ...extra,
});

const InventoryReceiptsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const linkedReceiptId = searchParams.get('receiptId');
  const linkedTransferId = searchParams.get('warehouseTransferId');
  const linkedProductId = searchParams.get('productId');
  const [transferPrefillNotice, setTransferPrefillNotice] = useState('');
  const [navigationError, setNavigationError] = useState('');
  const maxReceiptDate = formatLocalDateInput(new Date());
  const dispatch = useAppDispatch();
  const { products } = useAppSelector((s) => s.product);
  const { suppliers, groupId, epoch, isCurrent } = useSupplierDataScope();
  const username = useAppSelector((s) => s.auth.username);
  const roles = useAppSelector((s) => s.auth.roles);
  const { preferences: receiptTablePreferences, setWidth: setReceiptTableWidth, setVisible: setReceiptTableVisible, setDensity: setReceiptTableDensity, setFontSize: setReceiptTableFontSize, reset: resetReceiptTablePreferences } =
    useTablePreferences(RECEIPT_TABLE_PREFERENCES_KEY, username, RECEIPT_TABLE_COLUMNS);
  const visibleReceiptColumns = RECEIPT_TABLE_COLUMNS.filter(column => receiptTablePreferences.visibility[column.key]);
  const receiptColumnTotal = visibleReceiptColumns.reduce((sum, column) => sum + receiptTablePreferences.widths[column.key], 0);
  const receiptColumnScale = receiptColumnTotal > 0 ? 100 / receiptColumnTotal : 1;
  const canManagePayments = roles.includes('Керівник') || roles.includes('Адміністратор');

  const [receipts, setReceipts] = useState<InventoryReceipt[]>([]);
  const [selected, setSelected] = useState<InventoryReceipt | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReceiptStatusFilter>('all');
  const [listWarehouseId, setListWarehouseId] = useState('');
  const [loading, setLoading] = useState(false);
  const [receiptRange, setReceiptRange] = useState(calendarMonthRange);
  const [includeOutstanding, setIncludeOutstanding] = useState(true);
  const [listError, setListError] = useState('');
  const listRequestSequence = useRef({ value: 0 });
  const [showReceiptList, setShowReceiptList] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [copySourceId, setCopySourceId] = useState<number | null>(null);
  const [form, setForm] = useState<ReceiptForm>(emptyForm());
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [postAsPaid, setPostAsPaid] = useState(false);
  const [receiptActionError, setReceiptActionError] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(formatLocalDateInput(new Date()));
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [importPreview, setImportPreview] = useState<ImportedReceiptItem[] | null>(null);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrStatus, setOcrStatus] = useState('');
  const [ocrText, setOcrText] = useState('');
  const [ocrError, setOcrError] = useState('');
  const [ocrPreview, setOcrPreview] = useState<ReceiptOcrPreview | null>(null);
  const [printWithPrices, setPrintWithPrices] = useState(true);
  const [printCompactFlow, setPrintCompactFlow] = useState(false);
  const [sortColumn, setSortColumn] = useState<ReceiptSortColumn>('date');
  const [sortDirection, setSortDirection] = useState<ReceiptSortDirection>('desc');
  const fileRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const batchFileRef = useRef<HTMLInputElement>(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [batchFileWarnings, setBatchFileWarnings] = useState<string[]>([]);
  const [batchReceipts, setBatchReceipts] = useState<InventoryReceiptBatchReceipt[] | null>(null);
  const [batchWarehouseId, setBatchWarehouseId] = useState('');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchResultMessage, setBatchResultMessage] = useState('');

  const loadList = useCallback(async () => {
    const sequence = ++listRequestSequence.current.value;
    if (!receiptRange.from || !receiptRange.to || receiptRange.from > receiptRange.to) {
      setListError('Вкажіть коректний діапазон дат: початок не пізніше завершення.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    try {
      const res = await api.get<InventoryReceipt[]>('/inventoryreceipts', { params: {
        from: `${receiptRange.from}T00:00:00Z`, to: `${receiptRange.to}T23:59:59.999Z`, includeOutstanding,
      } });
      if (sequence !== listRequestSequence.current.value) return;
      setReceipts(res.data);
    } catch (error: unknown) {
      if (sequence !== listRequestSequence.current.value) return;
      setListError(isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message || 'Не вдалося завантажити накладні. Оновіть список.'
        : 'Не вдалося завантажити накладні. Оновіть список.');
    } finally {
      if (sequence === listRequestSequence.current.value) setLoading(false);
    }
  }, [receiptRange.from, receiptRange.to, includeOutstanding]);

  useEffect(() => {
    dispatch(fetchProducts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isCurrent && groupId !== null)
      dispatch(fetchSuppliers({ groupId, epoch }));
  }, [dispatch, epoch, groupId, isCurrent]);
  useEffect(() => {
    const sequence = listRequestSequence.current;
    void loadList();
    return () => { sequence.value++; };
  }, [loadList]);

  const openReceipt = async (id: number) => {
    const res = await api.get<InventoryReceipt>(`/inventoryreceipts/${id}`);
    setPostAsPaid(!!res.data.isPaid);
    setReceiptActionError('');
    console.log('InventoryReceiptsPage.openReceipt received', {
      id: res.data.id,
      invoiceNumber: res.data.invoiceNumber,
      isPaid: res.data.isPaid,
      itemCount: res.data.items?.length ?? 0,
    });
    console.log('InventoryReceiptsPage.openReceipt raw payload', res.data);
    setSelected(res.data);
    setShowReceiptList(false);
    setPrintWithPrices(shouldPrintReceiptPricesByDefault(res.data.supplierName));
  };

  useEffect(() => {
    setNavigationError('');
    if (linkedReceiptId === null) return;
    setSelected(null);
    setShowForm(false);
    if (!/^[1-9]\d*$/.test(linkedReceiptId) || !Number.isSafeInteger(Number(linkedReceiptId))) {
      setNavigationError('Некоректний ID накладної у посиланні.');
      return;
    }
    let active = true;
    api.get<InventoryReceipt>(`/inventoryreceipts/${linkedReceiptId}`).then(response => {
      if (!active) return;
      setSelected(response.data);
      setPostAsPaid(!!response.data.isPaid);
      setShowReceiptList(false);
      setPrintWithPrices(shouldPrintReceiptPricesByDefault(response.data.supplierName));
    }).catch((error: unknown) => {
      if (!active) return;
      setNavigationError(isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message || 'Не вдалося відкрити накладну з посилання. Перевірте її наявність і доступ.'
        : 'Не вдалося відкрити накладну з посилання. Перевірте її наявність і доступ.');
    });
    return () => { active = false; };
  }, [linkedReceiptId]);

  useEffect(() => {
    if (linkedTransferId === null || linkedReceiptId !== null) return;
    setNavigationError('');
    setTransferPrefillNotice('');
    if (!/^[1-9]\d*$/.test(linkedTransferId) || !Number.isSafeInteger(Number(linkedTransferId)) ||
        !linkedProductId || !/^[1-9]\d*$/.test(linkedProductId) || !Number.isSafeInteger(Number(linkedProductId))) {
      setNavigationError('Некоректне посилання на нестачу переміщення.');
      return;
    }
    let active = true;
    api.get<WarehouseTransferAvailabilityData>(`/inventorywarehousetransfers/${linkedTransferId}/availability`)
      .then(response => {
        if (!active) return;
        const item = response.data.items.find(row => row.productId === Number(linkedProductId));
        if (!item || item.shortage <= 0) {
          setNavigationError('Цей продукт більше не має нестачі у переміщенні. Оновіть його історію.');
          return;
        }
        const receiptDate = response.data.date.slice(0, 10);
        setEditingId(null);
        setCopySourceId(null);
        setSelected(null);
        setSaveError('');
        setImportPreview(null);
        setForm({
          ...emptyForm(), warehouseId: String(response.data.sourceWarehouseId),
          date: receiptDate, paymentDueDate: receiptDate,
          notes: `Прихід для переміщення між складами #${linkedTransferId}; перевірити фактичний документ і дату.`,
          items: [{
            ...emptyRow, entryKey: createReceiptEntryKey(), productId: String(item.productId),
            productQuery: item.productName, quantity: formatCalcValue(item.shortage, 3),
            weightType: item.weightType, unitPrice: '', lineTotal: '',
          }],
        });
        setTransferPrefillNotice(`Підготовлено прихід для переміщення #${linkedTransferId}: ${item.productName}, нестача ${item.shortage.toFixed(3)} ${getWeightTypeLabel(item.weightType)}. Запас ще не додано. Перевірте фактичні дату, номер, ціну й партію; збережіть і окремо проведіть накладну. Прихід має передувати переміщенню; не змінюйте історичну дату без первинного документа.`);
        setShowForm(true);
      }).catch((error: unknown) => {
        if (!active) return;
        setNavigationError(isAxiosError<{ message?: string }>(error)
          ? error.response?.data?.message || 'Не вдалося перевірити нестачу або доступ до переміщення.'
          : 'Не вдалося перевірити нестачу або доступ до переміщення.');
      });
    return () => { active = false; };
  }, [linkedTransferId, linkedProductId, linkedReceiptId]);

  const filtered = receipts.filter((r) => {
    const receiptStatus = getReceiptStatus(r);
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'Paid' && receiptStatus === 'Posted' && r.isPaid)
      || (statusFilter === 'Unpaid' && receiptStatus === 'Posted' && !r.isPaid)
      || (statusFilter !== 'Paid' && statusFilter !== 'Unpaid' && receiptStatus === statusFilter);
    const matchesSearch = !search
      || r.invoiceNumber.toLowerCase().includes(search.toLowerCase())
      || (r.supplierName || '').toLowerCase().includes(search.toLowerCase());
    const matchesWarehouse = listWarehouseId === ''
      || (listWarehouseId === 'unassigned' ? r.warehouseId == null : String(r.warehouseId) === listWarehouseId);
    return matchesStatus && matchesSearch && matchesWarehouse;
  });
  const sortedFiltered = React.useMemo(() => {
    const sorted = [...filtered];
    const direction = sortDirection === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      if (sortColumn === 'date') {
        return (new Date(a.date).getTime() - new Date(b.date).getTime()) * direction;
      }
      if (sortColumn === 'supplier') {
        return (a.supplierName || '').localeCompare(b.supplierName || '', 'uk') * direction;
      }
      if (sortColumn === 'invoice') {
        return (a.invoiceNumber || '').localeCompare(b.invoiceNumber || '', 'uk') * direction;
      }
      if (sortColumn === 'status') {
        return getReceiptStatus(a).localeCompare(getReceiptStatus(b)) * direction;
      }
      return (a.totalAmount - b.totalAmount) * direction;
    });
    return sorted;
  }, [filtered, sortColumn, sortDirection]);

  const warehouseReceiptGroups = React.useMemo(() => {
    const groups = new Map<number | null, { id: number | null; name: string; items: InventoryReceipt[] }>();
    sortedFiltered.forEach(receipt => {
      const id = receipt.warehouseId ?? null;
      if (!groups.has(id)) groups.set(id, {
        id, name: id === null ? 'Історичні документи без складу' : receipt.warehouseName || `Склад #${id}`, items: [],
      });
      groups.get(id)!.items.push(receipt);
    });
    return Array.from(groups.values()).sort((left, right) =>
      left.id === null ? 1 : right.id === null ? -1 :
        left.name.localeCompare(right.name, 'uk') || left.id - right.id);
  }, [sortedFiltered]);

  const toggleSort = (column: ReceiptSortColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const sortIndicator = (column: ReceiptSortColumn) => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const thSortStyle: React.CSSProperties = {
    ...thStyle,
    cursor: 'pointer',
    userSelect: 'none',
  };

  const startNew = () => {
    setEditingId(null);
    setCopySourceId(null);
    setForm(emptyForm());
    setSaveError('');
    setImportPreview(null);
    setShowForm(true);
  };

  const startEdit = async (r: InventoryReceipt) => {
    const res = await api.get<InventoryReceipt>(`/inventoryreceipts/${r.id}`);
    setEditingId(r.id);
    setCopySourceId(null);
    setSaveError('');
    setForm({
      date: formatLocalDateInput(res.data.date),
      paymentDueDate: formatLocalDateInput(res.data.paymentDueDate || res.data.date),
      supplierId: res.data.supplierId ? String(res.data.supplierId) : '',
      invoiceNumber: res.data.invoiceNumber,
      notes: res.data.notes,
      isPaid: !!res.data.isPaid,
      warehouseId: res.data.warehouseId ? String(res.data.warehouseId) : '',
      items: (res.data.items || []).map((i) => ({
        entryKey: createReceiptEntryKey(),
        productId: i.productId ? String(i.productId) : '',
        productQuery: i.productName || '',
        quantity: formatCalcValue(i.quantity, 4),
        unitPrice: formatCalcValue(i.unitPrice, 4),
        lineTotal: formatCalcValue(i.quantity * i.unitPrice, 2),
        weightType: i.weightType,
        packageCount: i.packageCount ? formatCalcValue(i.packageCount, 4) : '',
        packageWeight: i.packageWeight ? formatCalcValue(i.packageWeight, 4) : '',
        packagePrice: i.packagePrice ? formatCalcValue(i.packagePrice, 4) : '',
        lotNumber: i.lotNumber || '',
        manufactureDate: i.manufactureDate?.slice(0, 10) || '',
        expirationDate: i.expirationDate?.slice(0, 10) || '',
      })),
    });
    setImportPreview(null);
    setShowForm(true);
  };

  const startCopy = async (receipt: InventoryReceipt) => {
    setSaveError('');
    setReceiptActionError('');
    try {
      const response = await api.get<InventoryReceipt>(`/inventoryreceipts/${receipt.id}`);
      const source = response.data;
      setEditingId(null);
      setCopySourceId(source.id);
      setForm({
        date: formatLocalDateInput(source.date),
        paymentDueDate: formatLocalDateInput(source.paymentDueDate || source.date),
        supplierId: source.supplierId ? String(source.supplierId) : '',
        invoiceNumber: source.invoiceNumber,
        notes: source.notes,
        isPaid: false,
        warehouseId: source.warehouseId ? String(source.warehouseId) : '',
        items: (source.items || []).map((item) => ({
          entryKey: createReceiptEntryKey(),
          productId: item.productId ? String(item.productId) : '',
          productQuery: item.productName || '',
          quantity: formatCalcValue(item.quantity, 4),
          unitPrice: formatCalcValue(item.unitPrice, 4),
          lineTotal: formatCalcValue(item.quantity * item.unitPrice, 2),
          weightType: item.weightType,
          packageCount: item.packageCount ? formatCalcValue(item.packageCount, 4) : '',
          packageWeight: item.packageWeight ? formatCalcValue(item.packageWeight, 4) : '',
          packagePrice: item.packagePrice ? formatCalcValue(item.packagePrice, 4) : '',
          lotNumber: item.lotNumber || '',
          manufactureDate: item.manufactureDate?.slice(0, 10) || '',
          expirationDate: item.expirationDate?.slice(0, 10) || '',
        })),
      });
      setImportPreview(null);
      setShowForm(true);
    } catch (error: any) {
      setSaveError(error?.response?.data?.message || 'Не вдалося завантажити накладну для копіювання.');
    }
  };

  const addRow = (afterIndex: number) => setForm((f) => ({
    ...f,
    items: [
      ...f.items.slice(0, afterIndex + 1),
      { ...emptyRow, entryKey: createReceiptEntryKey() },
      ...f.items.slice(afterIndex + 1),
    ],
  }));
  const removeRow = (idx: number) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateRow = (idx: number, patch: Partial<ItemRow>, source: RowUpdateSource = 'other') =>
    setForm((f) => ({
      ...f,
      items: f.items.map((row, i) => (i === idx ? syncItemRow(row, patch, source) : row)),
    }));

  const total = form.items.reduce((sum, r) => sum + getRowComputedTotal(r), 0);

  const selectedSupplierVat = (() => {
    if (!form.supplierId) return 0;
    const sup = suppliers.find(s => String(s.id) === form.supplierId);
    return sup?.vatRate ?? 0;
  })();
  const vatAmount = selectedSupplierVat > 0 ? total * selectedSupplierVat / 100 : 0;
  const totalWithVat = total + vatAmount;
  const activeProductSearchItems = React.useMemo(
    () => getSelectableProductSearchItems(products, form.items.map((item) => item.productId)),
    [products, form.items]
  );
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError('');
    const hasIncompleteRow = form.items.some((row) => {
      const hasAnyValue = Boolean(
        row.productId || row.quantity || row.unitPrice || row.lineTotal ||
        row.packageCount || row.packageWeight || row.packagePrice ||
        row.lotNumber || row.manufactureDate || row.expirationDate
      );
      return hasAnyValue && (!row.productId || getResolvedQuantity(row) <= 0);
    });
    if (hasIncompleteRow) {
      setSaveError('Заповніть продукт і додатну кількість у кожному розпочатому рядку.');
      return;
    }
    setSaving(true);
    let savedId = editingId;
    try {
      const payload = {
        date: normalizeDateForApi(form.date),
        paymentDueDate: normalizeDateForApi(form.paymentDueDate),
        warehouseId: form.warehouseId ? Number(form.warehouseId) : null,
        supplierId: form.supplierId ? parseInt(form.supplierId) : null,
        receiptType: 'product',
        invoiceNumber: form.invoiceNumber,
        notes: form.notes,
        isPaid: form.isPaid,
        items: form.items
          .reduce<Array<{ productId: number | null; quantity: number; unitPrice: number; weightType: string; packageCount: number | null; packageWeight: number | null; packagePrice: number | null; lotNumber: string; manufactureDate: string | null; expirationDate: string | null }>>((acc, r) => {
            const quantity = getResolvedQuantity(r);
            const unitPrice = getResolvedUnitPrice(r);
            const lineTotal = evaluateCalcInput(r.lineTotal);
            const packageCount = evaluateCalcInput(r.packageCount);
            const packageWeight = evaluateCalcInput(r.packageWeight);
            const packagePrice = evaluateCalcInput(r.packagePrice);
            if (quantity <= 0) return acc;
            acc.push({
              productId: r.productId ? parseInt(r.productId, 10) : null,
              quantity,
              unitPrice: lineTotal !== null && quantity > 0 ? lineTotal / quantity : (unitPrice || 0),
              weightType: r.weightType,
              packageCount: packageCount !== null && packageCount > 0 ? packageCount : null,
              packageWeight: packageWeight !== null && packageWeight > 0 ? packageWeight : null,
              packagePrice: packagePrice !== null && packagePrice > 0 ? packagePrice : null,
              lotNumber: r.lotNumber.trim(),
              manufactureDate: r.manufactureDate || null,
              expirationDate: r.expirationDate || null,
            });
            return acc;
          }, []),
      };

      if (editingId) {
        await api.put(`/inventoryreceipts/${editingId}`, payload, { timeout: 30000 });
      } else {
        const response = await api.post<InventoryReceipt>('/inventoryreceipts', payload, { timeout: 30000 });
        savedId = response.data.id;
      }
    } catch (error: any) {
      const isTimeout = error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT';
      setSaveError(isTimeout
        ? 'Сервер не відповів за 30 секунд. Запит міг бути оброблений: оновіть список і перевірте накладну, перш ніж повторювати збереження.'
        : error?.response?.data?.message ||
          error?.response?.data?.title ||
          error?.message ||
          'Не вдалося зберегти накладну. Перевірте дані та спробуйте ще раз.');
      setSaving(false);
      return;
    }
    setSaving(false);
    setShowForm(false);
    setCopySourceId(null);
    await loadList();
    if (savedId) await openReceipt(savedId);
  };

  const postReceipt = async (receipt: InventoryReceipt) => {
    setReceiptActionError('');
    try {
      await api.post(`/inventoryreceipts/${receipt.id}/post`, { isPaid: postAsPaid }, {
        headers: { 'If-Match': `"${receipt.version}"` },
      });
      setPostAsPaid(false);
      await loadList();
      await openReceipt(receipt.id);
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 409 || status === 428) {
        await loadList();
        await openReceipt(receipt.id);
        setReceiptActionError(
          message
            ? `${message} Дані накладної оновлено — перевірте їх і повторіть проведення.`
            : 'Накладну змінено. Дані оновлено — перевірте їх і повторіть проведення.'
        );
      } else {
        setReceiptActionError(message || 'Не вдалося провести накладну.');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Скасувати накладну? Вона залишиться в історії, але більше не впливатиме на склад.')) return;
    const reason = window.prompt('Вкажіть причину скасування:');
    if (!reason?.trim()) return;
    await api.post(`/inventoryreceipts/${id}/cancel`, { reason: reason.trim() });
    if (selected?.id === id) setSelected(null);
    await loadList();
  };

  const addPayment = async () => {
    if (!selected) return;
    const amount = Number(paymentAmount.replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) {
      window.alert('Введіть коректну суму платежу.');
      return;
    }
    setPaymentError('');
    try {
      await api.post(`/inventoryreceipts/${selected.id}/payments`, {
        paymentDate: normalizeDateForApi(paymentDate),
        amount,
        method: paymentMethod,
        reference: paymentReference,
        notes: paymentNotes,
      }, { headers: { 'If-Match': `"${selected.version}"` } });
      setPaymentAmount('');
      setPaymentMethod('');
      setPaymentReference('');
      setPaymentNotes('');
      await loadList();
      await openReceipt(selected.id);
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 409 || status === 428) {
        await openReceipt(selected.id);
        setPaymentError(
          message
            ? `${message} Дані накладної оновлено — перевірте залишок і повторіть операцію.`
            : 'Накладну було змінено. Дані оновлено — перевірте залишок і повторіть операцію.'
        );
      } else {
        setPaymentError(message || 'Не вдалося додати платіж. Перевірте дані та спробуйте ще раз.');
      }
    }
  };

  const reversePayment = async (paymentId: number) => {
    if (!selected) return;
    const reason = window.prompt('Вкажіть причину сторнування платежу:');
    if (reason === null || !reason.trim()) return;
    await api.post(`/inventoryreceipts/${selected.id}/payments/${paymentId}/reverse`, { reason }, {
      headers: { 'If-Match': `"${selected.version}"` },
    });
    await loadList();
    await openReceipt(selected.id);
  };

  const handleImportFile = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post<ImportedReceiptItem[]>('/inventoryreceipts/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setImportPreview(res.data);
  };

  const applyImportPreview = () => {
    if (!importPreview) return;
    setForm((f) => ({
      ...f,
      items: importPreview.map((p) => ({
        entryKey: createReceiptEntryKey(),
        productId: p.productId ? String(p.productId) : '',
        productQuery: p.productName || '',
        quantity: formatCalcValue(p.quantity, 4),
        unitPrice: formatCalcValue(p.unitPrice, 4),
        lineTotal: formatCalcValue((p.quantity || 0) * (p.unitPrice || 0), 2),
        weightType: p.weightType || 'kg',
        packageCount: '',
        packageWeight: '',
        packagePrice: '',
        lotNumber: '',
        manufactureDate: '',
        expirationDate: '',
      })),
    }));
    setShowForm(true);
    setImportPreview(null);
  };

  const openPhotoImport = () => {
    setOcrText('');
    setOcrPreview(null);
    setOcrError('');
    setOcrProgress(0);
    setOcrStatus('');
    setOcrOpen(true);
  };

  const recognizeReceiptPhoto = async (file: File) => {
    setOcrBusy(true);
    setOcrError('');
    setOcrPreview(null);
    setOcrText('');
    setOcrStatus('Завантаження фото та аналіз у Google Document AI...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<ReceiptOcrPreview>('/inventoryreceipts/import-photo-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setOcrPreview(response.data);
      setOcrText(response.data.sourceText || '');
      setOcrStatus('Фото розпізнано через Google Document AI. Перевірте результат нижче.');
    } catch (error: any) {
      setOcrPreview(null);
      setOcrStatus('');
      setOcrError(error?.response?.data?.message || 'Не вдалося розпізнати фото через Google Document AI.');
    } finally {
      setOcrBusy(false);
      if (photoRef.current) photoRef.current.value = '';
    }
  };

  const analyzeOcrText = async () => {
    if (!ocrText.trim()) {
      setOcrError('Розпізнаний текст порожній.');
      return;
    }
    setOcrBusy(true);
    setOcrError('');
    try {
      const response = await api.post<ReceiptOcrPreview>('/inventoryreceipts/import-ocr-preview', { text: ocrText });
      setOcrPreview(response.data);
    } catch (error: any) {
      setOcrPreview(null);
      setOcrError(error?.response?.data?.message || 'Не вдалося проаналізувати розпізнаний текст.');
    } finally {
      setOcrBusy(false);
    }
  };

  const updateOcrRow = (lineNumber: number, patch: Partial<ReceiptOcrRow>) => {
    setOcrPreview((current) => current
      ? { ...current, rows: current.rows.map((row) => row.lineNumber === lineNumber ? { ...row, ...patch } : row) }
      : current);
  };

  const applyOcrPreview = () => {
    if (!ocrPreview) return;
    const includedRows = ocrPreview.rows.filter((row) => !row.excluded);
    const unresolved = includedRows.filter((row) => !row.productId || row.matchConfidence < .72 || row.quantity <= 0);
    if (unresolved.length > 0) {
      setOcrError('Для кожного рядка оберіть продукт і перевірте кількість.');
      return;
    }

    setEditingId(null);
    setSelected(null);
    setForm({
      date: ocrPreview.date || formatLocalDateInput(new Date()),
      paymentDueDate: ocrPreview.date || formatLocalDateInput(new Date()),
      supplierId: ocrPreview.supplierId ? String(ocrPreview.supplierId) : '',
      invoiceNumber: ocrPreview.invoiceNumber || '',
      notes: 'Імпортовано з фото накладної',
      isPaid: ocrPreview.isPaid,
      warehouseId: '',
      items: includedRows.map((row) => syncItemRow({
        entryKey: createReceiptEntryKey(),
        productId: String(row.productId),
        productQuery: row.productName || row.sourceProductName,
        quantity: formatCalcValue(row.quantity, 4),
        unitPrice: formatCalcValue(row.unitPrice, 4),
        lineTotal: formatCalcValue(row.lineTotal || row.quantity * row.unitPrice, 2),
        weightType: row.weightType || 'kg',
        packageCount: '',
        packageWeight: '',
        packagePrice: '',
        lotNumber: '',
        manufactureDate: '',
        expirationDate: '',
      }, {}, 'lineTotal')),
    });
    setOcrOpen(false);
    setOcrPreview(null);
    setShowForm(true);
  };

  const downloadBatchTemplate = async () => {
    setBatchError('');
    let url: string | null = null;
    try {
      const res = await api.get('/inventoryreceipts/import-batch-template', { responseType: 'blob' });
      url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CRM4Chef_шаблон_накладні_${formatLocalDateInput(new Date())}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      let message = 'Не вдалося завантажити шаблон.';
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          message = parsed?.message || message;
        } catch {
          // response wasn't JSON; keep generic message
        }
      } else if (data?.message) {
        message = data.message;
      } else if (error?.message) {
        message = error.message;
      }
      setBatchError(message);
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  const openBatchImport = () => {
    setBatchError('');
    setBatchFileWarnings([]);
    setBatchReceipts(null);
    setBatchResultMessage('');
    setBatchOpen(true);
  };

  const handleBatchFile = async (file: File) => {
    setBatchBusy(true);
    setBatchError('');
    setBatchResultMessage('');
    setBatchReceipts(null);
    setBatchFileWarnings([]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<InventoryReceiptBatchPreviewResult>('/inventoryreceipts/import-batch-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBatchReceipts(response.data.receipts.map((receipt) => ({ ...receipt, rows: receipt.rows.map((row) => ({ ...row })) })));
      setBatchFileWarnings(response.data.fileWarnings || []);
    } catch (error: any) {
      setBatchError(error?.response?.data?.message || 'Не вдалося обробити файл.');
    } finally {
      setBatchBusy(false);
      if (batchFileRef.current) batchFileRef.current.value = '';
    }
  };

  const recalcBatchRowTotal = (row: InventoryReceiptBatchRow): InventoryReceiptBatchRow => {
    const packageCount = row.packageCount || 0;
    const packageWeight = row.packageWeight || 0;
    const packagePrice = row.packagePrice || 0;
    const quantity = packageCount > 0 && packageWeight > 0 ? packageCount * packageWeight : row.quantity;
    const unitPrice = packagePrice > 0 && packageWeight > 0 ? packagePrice / packageWeight : row.unitPrice;
    return { ...row, quantity, unitPrice, lineTotal: quantity * unitPrice };
  };

  const updateBatchReceipt = (index: number, patch: Partial<InventoryReceiptBatchReceipt>) => {
    setBatchReceipts((current) => current ? current.map((receipt, i) => i === index ? { ...receipt, ...patch } : receipt) : current);
  };

  const updateBatchRow = (receiptIndex: number, rowIndex: number, patch: Partial<InventoryReceiptBatchRow>) => {
    setBatchReceipts((current) => current ? current.map((receipt, i) => {
      if (i !== receiptIndex) return receipt;
      return {
        ...receipt,
        rows: receipt.rows.map((row, ri) => ri === rowIndex ? recalcBatchRowTotal({ ...row, ...patch }) : row),
      };
    }) : current);
  };

  const isBatchRowValid = (row: InventoryReceiptBatchRow) => {
    if (row.excluded) return true;
    return !!row.productId && row.quantity > 0 && row.unitPrice >= 0;
  };

  const isBatchReceiptValid = (receipt: InventoryReceiptBatchReceipt) => {
    if (receipt.excluded) return true;
    if (!receipt.date || receipt.date > maxReceiptDate) return false;
    const includedRows = receipt.rows.filter((row) => !row.excluded);
    if (includedRows.length === 0) return false;
    return includedRows.every((row) => isBatchRowValid(row));
  };

  const submitBatch = async () => {
    if (!batchReceipts) return;
    const includedReceipts = batchReceipts.filter((receipt) => !receipt.excluded);
    if (includedReceipts.length === 0) {
      setBatchError('Немає жодної накладної для створення.');
      return;
    }
    if (!includedReceipts.every((receipt) => isBatchReceiptValid(receipt))) {
      setBatchError('Виправте помилки у накладних перед створенням.');
      return;
    }
    setBatchSubmitting(true);
    setBatchError('');
    try {
      const payload = {
        receipts: includedReceipts.map((receipt) => ({
          groupKey: receipt.groupKey,
          date: receipt.date,
          supplierId: receipt.supplierId,
          invoiceNumber: receipt.invoiceNumber,
          isPaid: receipt.isPaid,
          warehouseId: batchWarehouseId ? Number(batchWarehouseId) : null,
          notes: receipt.notes,
          items: receipt.rows.filter((row) => !row.excluded).map((row) => ({
            productId: row.productId,
            quantity: row.quantity,
            unitPrice: row.unitPrice,
            weightType: row.weightType,
            packageCount: row.packageCount && row.packageCount > 0 ? row.packageCount : null,
            packageWeight: row.packageWeight && row.packageWeight > 0 ? row.packageWeight : null,
            packagePrice: row.packagePrice && row.packagePrice > 0 ? row.packagePrice : null,
          })),
        })),
      };
      const response = await api.post('/inventoryreceipts/import-batch-create', payload);
      const created = response.data?.created || [];
      setBatchResultMessage(`Створено чернеток накладних: ${created.length} (ID: ${created.map((c: any) => c.id).join(', ')}). Склад зміниться після проведення кожної накладної.`);
      await loadList();
      setBatchReceipts(null);
      setBatchOpen(false);
      if (created[0]?.id) await openReceipt(created[0].id);
    } catch (error: any) {
      const data = error?.response?.data;
      const errorsList: string[] = data?.errors || [];
      setBatchError([data?.message, ...errorsList].filter(Boolean).join(' '));
    } finally {
      setBatchSubmitting(false);
    }
  };

  const handleExportExcel = async (id: number) => {
    const res = await api.get(`/inventoryreceipts/${id}/export-excel`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Накладна_${id}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const buildReceiptPrintHtml = (receipt: InventoryReceipt, includePrices: boolean, compactFlow: boolean) => {
    const supplierVatRate = receipt.supplierVatRate ?? 0;
    const showVatColumn = includePrices && supplierVatRate > 0;
    const totalVatAmount = showVatColumn ? calculateReceiptVatAmount(receipt.totalAmount, supplierVatRate) : 0;
    const allItems = receipt.items || [];
    const rowsPerSheet = includePrices ? 14 : 16;
    const sheets: InventoryReceiptItem[][] = compactFlow
      ? [allItems]
      : [];
    if (!compactFlow) {
      for (let index = 0; index < allItems.length; index += rowsPerSheet) {
        sheets.push(allItems.slice(index, index + rowsPerSheet));
      }
    }
    if (sheets.length === 0) {
      sheets.push([]);
    }

    return `
      <div class="pdf-receipt-root ${compactFlow ? 'compact-flow' : 'split-pages'}">
        ${sheets.map((sheetItems, sheetIndex) => {
          const pageNumber = sheetIndex + 1;
          const totalPages = sheets.length;
          const rowsHtml = sheetItems
            .map((i) => {
              const lineTotal = i.quantity * i.unitPrice;
              const lineVat = calculateReceiptVatAmount(lineTotal, supplierVatRate);
              const lineTotalWithVat = lineTotal + lineVat;
              return `
                <tr>
                  <td>${i.productName || '—'}</td>
                  <td style="text-align:right;">${i.quantity.toFixed(2)}</td>
                  <td style="text-align:center;">${getWeightTypeLabel(i.weightType)}</td>
                  <td style="text-align:right; min-width:76px;">${includePrices && i.unitPrice > 0 ? i.unitPrice.toFixed(2) : ''}</td>
                  ${showVatColumn ? `<td style="text-align:right;">${lineVat > 0 ? `${lineVat.toFixed(2)} ₴` : ''}</td>` : ''}
                  <td style="text-align:right; min-width:86px;">${includePrices && i.unitPrice > 0 ? (showVatColumn ? lineTotalWithVat.toFixed(2) : lineTotal.toFixed(2)) : ''}</td>
                </tr>
              `;
            })
            .join('');

          return `
            <section class="receipt-sheet">
              <div class="receipt-top">
                <div class="receipt-title-row">
                  <h2>Накладна №${receipt.invoiceNumber}</h2>
                  ${compactFlow ? '' : `<span class="page-index">Сторінка ${pageNumber} / ${totalPages}</span>`}
                </div>
                <div class="receipt-meta-grid">
                  <div><strong>Компанія:</strong> Весільний Зал</div>
                  <div><strong>Дата:</strong> ${new Date(receipt.date).toLocaleDateString('uk-UA')}</div>
                  <div><strong>Постачальник:</strong> ${receipt.supplierName || '—'}</div>
                  ${receipt.notes ? `<div><strong>Примітки:</strong> ${receipt.notes}</div>` : '<div><strong>Примітки:</strong> —</div>'}
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Продукт</th>
                    <th style="text-align:right;">К-сть</th>
                    <th style="text-align:center;">Од.</th>
                    <th style="text-align:right; min-width:76px;">Ціна</th>
                    ${showVatColumn ? '<th style="text-align:right;">ПДВ</th>' : ''}
                    <th style="text-align:right; min-width:86px;">Сума</th>
                  </tr>
                </thead>
                <tbody>
                  ${rowsHtml || `<tr><td colspan="${showVatColumn ? 6 : 5}" style="text-align:center;color:#7f8c8d;">Позиції відсутні</td></tr>`}
                </tbody>
                ${includePrices && pageNumber === totalPages
                  ? `<tfoot>
                      ${showVatColumn ? `<tr><td colspan="5" style="text-align:right;">Разом без ПДВ:</td><td style="text-align:right;">${receipt.totalAmount.toFixed(2)} ₴</td></tr>
                      <tr><td colspan="5" style="text-align:right;">ПДВ (${supplierVatRate}%):</td><td style="text-align:right;">${totalVatAmount.toFixed(2)} ₴</td></tr>` : ''}
                      <tr><td colspan="${showVatColumn ? 5 : 4}" style="text-align:right;">Разом з ПДВ:</td><td style="text-align:right;">${(receipt.totalAmount + totalVatAmount).toFixed(2)} ₴</td></tr>
                    </tfoot>`
                  : ''}
              </table>
              <div class="receipt-bottom">
                <div></div>
                <div>Отримав(ла): ____________________</div>
              </div>
            </section>
          `;
        }).join('')}
      </div>
    `;
  };

  const getReceiptPrintStyles = () => `
    .pdf-receipt-root { font-family: Arial, sans-serif; width: 100%; color: #2c3e50; }
    body, body * { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .receipt-sheet { border: 2px solid #d7e3ef; border-radius: 10px; padding: 12px; margin-bottom: 14px; background: #fff; }
    .pdf-receipt-root.split-pages .receipt-sheet { page-break-after: always; }
    .pdf-receipt-root.split-pages .receipt-sheet:last-child { page-break-after: auto; }
    .receipt-top { border-bottom: 2px solid #2980b9; padding-bottom: 8px; margin-bottom: 10px; background: linear-gradient(180deg, #f7fbff 0%, #ffffff 100%); }
    .receipt-title-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 6px; }
    .receipt-title-row h2 { margin: 0; font-size: 20px; color: #1f4f7a; }
    .page-index { font-size: 12px; color: #5d6d7e; font-weight: 600; }
    .receipt-meta-grid { display: grid; grid-template-columns: repeat(2, minmax(220px, 1fr)); gap: 4px 12px; font-size: 12px; color: #2f3f4f; }
    .pdf-receipt-root table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    .pdf-receipt-root thead { display: table-header-group; }
    .pdf-receipt-root th, .pdf-receipt-root td { border: 1px solid #dfe6e9; padding: 7px 8px; font-size: 12px; }
    .pdf-receipt-root th { background: #eef4fa; color: #2c3e50; text-align: left; }
    .pdf-receipt-root tfoot td { font-weight: 700; background: #f9fbfd; }
    .receipt-bottom { border-top: 2px solid #2980b9; margin-top: 12px; padding-top: 8px; display: flex; justify-content: space-between; gap: 12px; font-size: 12px; color: #2f3f4f; }
    .pdf-receipt-root.compact-flow .receipt-sheet { border-width: 1px; border-radius: 8px; padding: 8px; margin-bottom: 8px; }
    .pdf-receipt-root.compact-flow .receipt-top { padding-bottom: 6px; margin-bottom: 6px; }
    .pdf-receipt-root.compact-flow .receipt-title-row h2 { font-size: 17px; }
    .pdf-receipt-root.compact-flow .receipt-meta-grid { font-size: 11px; gap: 3px 10px; }
    .pdf-receipt-root.compact-flow th, .pdf-receipt-root.compact-flow td { padding: 5px 6px; font-size: 11px; }
    .pdf-receipt-root.compact-flow .receipt-bottom { margin-top: 8px; padding-top: 6px; font-size: 11px; }
    @media print {
      .pdf-receipt-root.split-pages .receipt-sheet { break-inside: avoid; page-break-inside: avoid; margin-bottom: 0; }
      .pdf-receipt-root.compact-flow .receipt-sheet { break-inside: auto; page-break-inside: auto; margin-bottom: 0; }
    }
  `;

  const handleExportPdf = async (receipt: InventoryReceipt, withPrices: boolean, compactFlow: boolean) => {
    const host = document.createElement('div');
    host.style.position = 'fixed';
    host.style.left = '-10000px';
    host.style.top = '0';
    host.style.width = '1122px';
    host.style.background = '#fff';
    host.style.padding = '20px';
    host.style.boxSizing = 'border-box';
    host.innerHTML = `<style>${getReceiptPrintStyles()}</style>${buildReceiptPrintHtml(receipt, withPrices, compactFlow)}`;
    document.body.appendChild(host);

    try {
      const canvas = await html2canvas(host, { scale: 2, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      doc.save(`Накладна_${receipt.invoiceNumber || receipt.id}.pdf`);
    } finally {
      document.body.removeChild(host);
    }
  };

  const handlePrint = () => {
    if (!selected) return;
    const includePrices = printWithPrices;
    const compactFlow = printCompactFlow;
    void openAndPrintHtml(`
      <html>
        <head>
          <title>Накладна №${selected.invoiceNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #2c3e50; }
            ${getReceiptPrintStyles()}
          </style>
        </head>
        <body>
          ${buildReceiptPrintHtml(selected, includePrices, compactFlow)}
        </body>
      </html>
    `, { windowFeatures: 'width=1100,height=800' });
  };

  return (
    <div className="page-shell page-shell--wide inventory-receipts-page" style={{ maxWidth: '1500px' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Прихідні накладні</h2>
      {navigationError && <p role="alert" style={{ color: 'var(--crm-danger)' }}>{navigationError}</p>}

      <div className="no-print responsive-split-layout" style={{ marginTop: '16px' }}>
        {/* Left panel: list */}
        <div style={{ flex: '0 0 420px' }}>
          <div className="inventory-receipts-toolbar" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Пошук за постачальником/№ накладної"
              style={{ flex: 1, padding: '8px' }}
            />
            <button onClick={startNew} style={btn('success')}>+ Нова</button>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: 'var(--crm-text)' }}>
            <span>Показати:</span>
            <select
              aria-label="Фільтр за статусом накладної"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as ReceiptStatusFilter)}
              style={{ flex: 1, minWidth: 0, padding: '7px 8px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
            >
              <option value="all">Усі статуси ({receipts.length})</option>
              <option value="Draft">Чернетки ({receipts.filter((r) => getReceiptStatus(r) === 'Draft').length})</option>
              <option value="Posted">Проведені ({receipts.filter((r) => getReceiptStatus(r) === 'Posted').length})</option>
              <option value="Cancelled">Скасовані ({receipts.filter((r) => getReceiptStatus(r) === 'Cancelled').length})</option>
              <option value="Paid">Оплачені ({receipts.filter((r) => getReceiptStatus(r) === 'Posted' && r.isPaid).length})</option>
              <option value="Unpaid">Неоплачені ({receipts.filter((r) => getReceiptStatus(r) === 'Posted' && !r.isPaid).length})</option>
            </select>
          </label>
          <fieldset style={{ margin: '0 0 12px', minWidth: 0, border: '1px solid var(--crm-border)' }}>
            <legend>Період накладних</legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <label style={{ minWidth: 0 }}>Від
                <input type="date" required value={receiptRange.from} style={{ display: 'block', minHeight: 44, fontSize: 16, maxWidth: '100%' }}
                  onChange={event => setReceiptRange(current => ({ ...current, from: event.target.value }))} />
              </label>
              <label style={{ minWidth: 0 }}>До
                <input type="date" required value={receiptRange.to} style={{ display: 'block', minHeight: 44, fontSize: 16, maxWidth: '100%' }}
                  onChange={event => setReceiptRange(current => ({ ...current, to: event.target.value }))} />
              </label>
              <button type="button" onClick={() => setReceiptRange(calendarMonthRange())} style={{ ...btn('muted'), minHeight: 44 }}>Поточний місяць</button>
              <button type="button" disabled={loading} onClick={() => void loadList()} style={{ ...btn('muted'), minHeight: 44 }}>Оновити список</button>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, marginTop: 8 }}>
              <input type="checkbox" checked={includeOutstanding} onChange={event => setIncludeOutstanding(event.target.checked)} />
              Також неоплачені та непроведені поза періодом
            </label>
            <p>Типово — поточний календарний місяць плюс неоплачені проведені накладні та чернетки з інших дат. Скасовані поза періодом не додаються.</p>
          </fieldset>
          {listError && <p role="alert">{listError}</p>}
          <TablePreferencesPanel
            columns={RECEIPT_TABLE_COLUMNS}
            preferences={receiptTablePreferences}
            onWidthChange={setReceiptTableWidth}
            onVisibilityChange={setReceiptTableVisible}
            onDensityChange={setReceiptTableDensity}
            onFontSizeChange={setReceiptTableFontSize}
            onReset={resetReceiptTablePreferences}
          />
          <div className="inventory-receipts-list-warehouse-filter">
            <InventoryWarehouseSelect
              value={listWarehouseId}
              onChange={setListWarehouseId}
              label="Склад у списку накладних"
              allLabel="Усі склади"
              includeUnassigned
              required={false}
            />
          </div>
          <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleImportFile(f);
            if (fileRef.current) fileRef.current.value = '';
          }} />
          <input ref={photoRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) recognizeReceiptPhoto(file);
          }} />
          <input ref={batchFileRef} type="file" accept=".xlsx,.xlsm" style={{ display: 'none' }} onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleBatchFile(file);
          }} />
          <div className="inventory-receipts-import-actions" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className="inventory-receipts-import-button" onClick={() => fileRef.current?.click()} style={{ ...btn('accent'), flex: 1 }}>📤 Імпорт Excel</button>
            <button className="inventory-receipts-import-button" onClick={openPhotoImport} style={{ ...btn('primary'), flex: 1 }}>📷 Імпорт з фото</button>
            <button className="inventory-receipts-import-button" onClick={openBatchImport} style={{ ...btn('danger'), flex: 1 }}>📊 Імпорт з Excel (кілька накладних)</button>
          </div>

          <button
            type="button"
            className="inventory-receipts-mobile-list-toggle"
            aria-expanded={showReceiptList}
            onClick={() => setShowReceiptList((visible) => !visible)}
          >
            <span>Накладні за складами</span>
            <span>{sortedFiltered.length} {showReceiptList ? '▲' : '▼'}</span>
          </button>
          <div className={`inventory-receipts-mobile-list ${showReceiptList ? 'is-open' : ''}`}>
          <div className="inventory-receipts-list-panel" style={{ maxHeight: '600px', overflowY: 'auto', background: 'var(--crm-surface)', boxShadow: 'var(--crm-shadow-sm)' }}>
            {!loading && !listError && warehouseReceiptGroups.map(group => <section key={group.id ?? 'unassigned'}
              aria-labelledby={`receipt-warehouse-${group.id ?? 'unassigned'}`} style={{ marginBottom: 16 }}>
            <h3 id={`receipt-warehouse-${group.id ?? 'unassigned'}`} style={{ padding: '12px 8px', margin: 0, overflowWrap: 'anywhere', background: 'var(--crm-surface-alt)' }}>
              {group.id === null ? group.name : `Склад: ${group.name}`} · {group.items.length}
            </h3>
            <table aria-label={group.id === null ? group.name : `Накладні складу ${group.name} (#${group.id})`}
              className={`crm-table ${receiptTablePreferences.density === 'compact' ? 'crm-table--compact' : ''}`} style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', background: 'var(--crm-surface)', fontSize: `${receiptTablePreferences.fontSize}px` }}>
              <colgroup>
                {visibleReceiptColumns.map(column => (
                  <col key={column.key} style={{ width: `${(receiptTablePreferences.widths[column.key] * receiptColumnScale).toFixed(2)}%` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {receiptTablePreferences.visibility.date && <th style={{ ...thSortStyle, ...thStyle }} onClick={() => toggleSort('date')}>Дата{sortIndicator('date')}</th>}
                  {receiptTablePreferences.visibility.supplier && <th style={{ ...thSortStyle, ...thStyle }} onClick={() => toggleSort('supplier')}>Постачальник{sortIndicator('supplier')}</th>}
                  {receiptTablePreferences.visibility.invoice && <th style={{ ...thSortStyle, ...thStyle }} onClick={() => toggleSort('invoice')}>№{sortIndicator('invoice')}</th>}
                  {receiptTablePreferences.visibility.status && <th style={{ ...thSortStyle, ...thStyle }} onClick={() => toggleSort('status')}>Статус / оплата{sortIndicator('status')}</th>}
                  {receiptTablePreferences.visibility.amount && <th style={{ ...thSortStyle, ...thStyle, textAlign: 'right' }} onClick={() => toggleSort('amount')}>Сума{sortIndicator('amount')}</th>}
                </tr>
              </thead>
              <tbody>
                {group.items.map((r) => {
                  const status = getReceiptStatus(r);
                  const presentation = receiptStatusPresentation[status];
                  return (
                  <tr
                    key={r.id}
                    className={`${selected?.id === r.id ? 'is-selected' : ''} ${status === 'Cancelled' ? 'is-cancelled' : ''}`}
                    onClick={() => openReceipt(r.id)}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    {receiptTablePreferences.visibility.date && <td style={tdStyle}>
                      {new Date(r.date).toLocaleDateString('uk-UA')}
                      {(r.date.slice(0, 10) < receiptRange.from || r.date.slice(0, 10) > receiptRange.to) &&
                        <div style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>
                          Поза періодом · {status === 'Draft' ? 'непроведена' : 'неоплачена'}
                        </div>}
                    </td>}
                    {receiptTablePreferences.visibility.supplier && <td style={tdStyle}>{r.supplierName || '—'}</td>}
                    {receiptTablePreferences.visibility.invoice && <td style={{ ...tdStyle, textDecoration: status === 'Cancelled' ? 'line-through' : undefined }}>{r.invoiceNumber}</td>}
                    {receiptTablePreferences.visibility.status && <td style={tdStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                        <span className={`inventory-receipt-status ${presentation.className}`}>
                          {presentation.label}
                        </span>
                        {status === 'Posted' && (
                          <span className={r.isPaid ? 'inventory-receipt-payment--paid' : 'inventory-receipt-payment--unpaid'}>
                            {r.isPaid ? 'Оплачено' : `Борг ${r.outstandingAmount.toFixed(2)} ₴`}
                          </span>
                        )}
                      </div>
                    </td>}
                    {receiptTablePreferences.visibility.amount && <td style={{ ...tdStyle, textAlign: 'right' }}>{r.totalAmount.toFixed(2)} ₴</td>}
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </section>)}
            {(loading || (!listError && warehouseReceiptGroups.length === 0)) &&
              <p style={{ padding: 16, textAlign: 'center', color: 'var(--crm-text-muted)' }}>
                {loading ? 'Завантаження...' : 'Накладних немає'}
              </p>}
          </div>
          </div>
        </div>

        {/* Right panel: detail */}
        <div style={{ flex: 1 }}>
          {importPreview && (
            <div className="inventory-receipts-form" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '16px' }}>
              <h3>Попередній перегляд імпорту</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Продукт (з файлу)</th>
                    <th style={thStyle}>Знайдено</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Ціна</th>
                    <th style={thStyle}>Од.</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.map((p, idx) => (
                    <tr key={idx}>
                      <td style={tdStyle}>{p.productName}</td>
                      <td style={{ ...tdStyle, color: p.matched ? 'var(--crm-success)' : 'var(--crm-danger)' }}>{p.matched ? 'так' : 'не знайдено'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{p.quantity.toFixed(2)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{p.unitPrice}</td>
                      <td style={tdStyle}>{getWeightTypeLabel(p.weightType)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                <button onClick={applyImportPreview} style={btn('success')}>Використати у новій накладній</button>
                <button onClick={() => setImportPreview(null)} style={btn('muted')}>Скасувати</button>
              </div>
            </div>
          )}

          {showForm && (
            <form
              onSubmit={handleSave}
              onInvalidCapture={() => setSaveError('Заповніть обов’язкові поля: дата, термін оплати та номер накладної.')}
              className="inventory-receipts-card"
              style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '16px' }}
            >
              <h3>{editingId ? 'Редагування накладної' : copySourceId ? 'Копія накладної' : 'Нова накладна'}</h3>
              {transferPrefillNotice && <p role="status">{transferPrefillNotice}</p>}
              {linkedTransferId && <p><Link to="/management/inventory-warehouse-transfers">Повернутися до переміщень між складами</Link></p>}
              {saveError && <p role="alert" style={{ color: 'var(--crm-danger)', fontWeight: 600 }}>{saveError}</p>}
              {!editingId && (
                <p style={{ color: 'var(--crm-text-muted)' }}>
                  {copySourceId ? `Копія накладної №${form.invoiceNumber} (#${copySourceId}). ` : ''}
                  Накладна збережеться як чернетка й не вплине на склад, доки ви окремо її не проведете.
                  {copySourceId ? ' Історія оплат не копіюється; перевірте дату, номер і партії перед збереженням.' : ''}
                </p>
              )}
              <div className="inventory-receipts-form-grid" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <InventoryWarehouseSelect value={form.warehouseId} onChange={(warehouseId) => setForm({ ...form, warehouseId })} />
                <label style={{ flex: '1 1 280px', minWidth: '280px' }}>Дата:
                  <input type="date" required max={maxReceiptDate} value={form.date} onChange={(e) => {
                    const supplier = suppliers.find(item => String(item.id) === form.supplierId);
                    setForm({ ...form, date: e.target.value, paymentDueDate: addCalendarDays(e.target.value, supplier?.paymentTermDays ?? 0) });
                  }} style={{ display: 'block', padding: '6px', width: '100%' }} />
                </label>
                <label style={{ flex: '1 1 280px', minWidth: '280px' }}>Термін оплати:
                  <input type="date" required value={form.paymentDueDate} onChange={(e) => setForm({ ...form, paymentDueDate: e.target.value })} style={{ display: 'block', padding: '6px', width: '100%' }} />
                </label>
                <label style={{ flex: '1 1 280px', minWidth: '280px' }}>Постачальник:
                  <SearchSelect
                    items={suppliers.map((s) => ({ id: String(s.id), name: s.name }))}
                    value={form.supplierId}
                    onChange={(id) => {
                      const supplier = suppliers.find(item => String(item.id) === id);
                      setForm({ ...form, supplierId: id, paymentDueDate: addCalendarDays(form.date, supplier?.paymentTermDays ?? 0) });
                    }}
                    placeholder="🔍 Пошук постачальника..."
                    minWidth={0}
                  />
                </label>
                <label style={{ flex: '1 1 280px', minWidth: '280px' }}>№ накладної:
                  <input required value={form.invoiceNumber} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} style={{ display: 'block', padding: '6px', width: '100%' }} />
                </label>
                <label style={{ flex: '2 1 420px', minWidth: '280px' }}>Примітки:
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ display: 'block', padding: '6px', width: '100%' }} />
                </label>
              </div>
              <div className="inventory-receipts-entry-list crm-position-list">
                  {form.items.map((row, idx) => (
                    <section className="inventory-receipts-entry-card crm-position-card" key={row.entryKey}>
                      <div className="inventory-receipts-product-field">
                        <div className="inventory-receipts-product-search-row">
                          <span className="inventory-receipts-product-count" aria-label={`Позиція ${idx + 1}`}>{idx + 1}</span>
                          <ItemQuantityField
                            appearance="outlined"
                            items={activeProductSearchItems}
                            value={row.productId}
                            onChange={(id) => {
                              const selectedProduct = products.find((p) => String(p.id) === id);
                              updateRow(idx, {
                                productId: id,
                                productQuery: selectedProduct?.name || '',
                                weightType: selectedProduct?.weightType || 'kg',
                              });
                            }}
                            quantity={row.quantity}
                            onQuantityChange={(quantity) => updateRow(idx, { quantity }, 'quantity')}
                            label="Продукт"
                            quantityLabel="Кількість"
                            unit={getWeightTypeLabel(row.weightType)}
                            quantityInputType="text"
                            quantityInputMode="decimal"
                            quantityDisabled={hasPackageMode(row)}
                            quantityRequired={false}
                            placeholder="Пошук продукту..."
                            ariaLabel={`Продукт у рядку ${idx + 1}`}
                          />
                        </div>
                      </div>
                      <div className="inventory-receipts-calculation-grid">
                        <label className="inventory-receipts-float-field" title="Кількість упаковок"><span>Кількість упаковок</span><input aria-label="Кількість упаковок" type="text" inputMode="decimal" value={row.packageCount} onChange={(e) => updateRow(idx, { packageCount: e.target.value }, 'packageCount')} /></label>
                        <label className="inventory-receipts-float-field" title="Вага 1 упаковки"><span>Вага 1 упаковки</span><input aria-label="Вага 1 упаковки" type="text" inputMode="decimal" value={row.packageWeight} onChange={(e) => updateRow(idx, { packageWeight: e.target.value }, 'packageWeight')} /></label>
                        <label className="inventory-receipts-float-field" title="Ціна упаковки"><span>Ціна упаковки</span><input aria-label="Ціна упаковки" type="text" inputMode="decimal" value={row.packagePrice} onChange={(e) => updateRow(idx, { packagePrice: e.target.value }, 'packagePrice')} /></label>
                        <label className="inventory-receipts-float-field" title="Ціна за одиницю"><span>Ціна за одиницю</span><ReceiptUnitPriceInput
                          value={row.unitPrice}
                          disabled={hasPackageMode(row)}
                          onChange={(value) => updateRow(idx, { unitPrice: value }, 'unitPrice')}
                          onCommit={(value) => updateRow(idx, { unitPrice: value }, 'unitPrice')}
                        /></label>
                        <label className="inventory-receipts-float-field inventory-receipts-line-total" title="Сума"><span>Сума</span><input aria-label="Сума" type="text" inputMode="decimal" value={row.lineTotal} onChange={(e) => updateRow(idx, { lineTotal: e.target.value }, 'lineTotal')} placeholder="0,00" /></label>
                      </div>
                      <div className="inventory-receipts-entry-footer">
                        <ReceiptDetails
                          title="Партія та терміни · необов’язково"
                          initiallyOpen={Boolean(row.lotNumber || row.manufactureDate || row.expirationDate)}
                        >
                          <div className="inventory-receipts-entry-grid">
                            <label>Номер партії<input value={row.lotNumber} onChange={(e) => updateRow(idx, { lotNumber: e.target.value })} /></label>
                            <label>Дата виробництва<input type="date" value={row.manufactureDate} onChange={(e) => updateRow(idx, { manufactureDate: e.target.value })} /></label>
                            <label>Придатний до<input type="date" value={row.expirationDate} onChange={(e) => updateRow(idx, { expirationDate: e.target.value })} /></label>
                          </div>
                        </ReceiptDetails>
                        <div className="inventory-receipts-product-controls">
                          <label className="inventory-receipts-product-unit">
                            <span>Од. вим.</span>
                            <select aria-label="Одиниця виміру" value={row.weightType} onChange={(e) => updateRow(idx, { weightType: e.target.value })}>
                              {WEIGHT_TYPES.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                            </select>
                          </label>
                          <button
                            type="button"
                            className="inventory-receipts-remove-button"
                            onClick={() => removeRow(idx)}
                            aria-label={`Видалити позицію ${idx + 1}`}
                            title="Видалити позицію"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <div className="crm-position-insert">
                        <button type="button" className="crm-position-insert__button" onClick={() => addRow(idx)}
                          aria-label={`Додати позицію після ${idx + 1}`}>+ Додати нижче</button>
                      </div>
                    </section>
                  ))}
              </div>
              {!form.items.length && (
                <div className="crm-position-insert">
                  <button type="button" className="crm-position-insert__button" onClick={() => addRow(-1)}>+ Додати позицію</button>
                </div>
              )}
              <div style={{ fontWeight: 'bold', marginBottom: selectedSupplierVat > 0 ? '4px' : '12px' }}>Разом без ПДВ: {total.toFixed(2)} ₴</div>
              {selectedSupplierVat > 0 && (
                <div style={{ background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-md)', padding: '8px 12px', marginBottom: '12px', fontSize: '13px', color: 'var(--crm-warning)', display: 'inline-flex', flexDirection: 'column', gap: '2px' }}>
                  <div>Без ПДВ: {total.toFixed(2)} ₴</div>
                  <div>ПДВ ({selectedSupplierVat}%): {vatAmount.toFixed(2)} ₴</div>
                  <div style={{ fontWeight: 'bold' }}>З ПДВ: {totalWithVat.toFixed(2)} ₴</div>
                </div>
              )}
              <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginBottom: '12px' }}>
                Можна заповнювати через "Сума" — тоді ціна за од. (і ціна/шт у режимі упаковки) рахується автоматично.
              </div>
              <div className="inventory-receipts-form-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '999px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)' }}>
                  <input
                    type="checkbox"
                    checked={form.isPaid}
                    onChange={(e) => setForm({ ...form, isPaid: e.target.checked })}
                  />
                  Після проведення позначити як повністю оплачену
                </label>
                <button type="submit" disabled={saving} style={{ ...btn(saving ? 'muted' : 'success'), borderRadius: '999px' }}>
                  {saving ? 'Збереження…' : 'Зберегти'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} style={{ ...btn('muted'), borderRadius: '999px' }}>Скасувати</button>
              </div>
            </form>
          )}

          {selected && !showForm && (
           <div className="print-area inventory-receipts-detail" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)' }}>
              {(() => {
                const selectedVatRate = selected.supplierVatRate ?? 0;
                const showVatColumn = selectedVatRate > 0;
                const selectedVatAmount = showVatColumn ? calculateReceiptVatAmount(selected.totalAmount, selectedVatRate) : 0;

                return (
                  <>
              <div className="inventory-receipts-detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Накладна №{selected.invoiceNumber} {selected.status === 'Cancelled' ? '· СКАСОВАНО' : selected.status === 'Draft' ? '· ЧЕРНЕТКА' : '· ПРОВЕДЕНО'}</h3>
                <div className="no-print inventory-receipts-detail-actions" style={{ display: 'flex', gap: '8px' }}>
                  {selected.status === 'Draft' && <button onClick={() => startEdit(selected)} style={btn('warning')}>Редагувати</button>}
                  {selected.status === 'Draft' && <button onClick={() => postReceipt(selected)} style={btn('primary')}>Провести накладну</button>}
                  <button type="button" onClick={() => startCopy(selected)} style={btn('info')}>Створити копію як чернетку</button>
                  {selected.status !== 'Cancelled' && <button onClick={() => handleDelete(selected.id)} style={btn('danger')}>Скасувати</button>}
                  <button onClick={() => handleExportExcel(selected.id)} style={btn('success')}>Експорт Excel</button>
                  <button onClick={() => handleExportPdf(selected, printWithPrices, printCompactFlow)} style={btn('accent')}>Експорт PDF</button>
                  <label className="inventory-receipts-print-toggle" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderRadius: '999px' }}>
                    <input type="checkbox" checked={printWithPrices} onChange={(e) => setPrintWithPrices(e.target.checked)} />
                    Ціна і Сума
                  </label>
                  <label className="inventory-receipts-print-toggle" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderRadius: '999px' }}>
                    <input type="checkbox" checked={printCompactFlow} onChange={(e) => setPrintCompactFlow(e.target.checked)} />
                    Економно без розбивки
                  </label>
                  <button onClick={handlePrint} style={btn('muted')}>Друк</button>
                </div>
              </div>
              {selected.status !== 'Draft' && selected.status !== 'Cancelled' && <p style={{ color: 'var(--crm-text-muted)' }}>Проведений документ незмінний. Для виправлення скасуйте його та створіть новий.</p>}
              {selected.postedAtUtc && <p>Проведено: {new Date(selected.postedAtUtc).toLocaleString('uk-UA')} · Автор: {selected.postedByUsername || 'історичний запис'}</p>}
              {selected.status === 'Draft' && (
                <div className="no-print" style={{ padding: 12, margin: '12px 0', background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-sm)' }}>
                  <p style={{ marginTop: 0 }}>Чернетка не змінює склад і фінансові звіти. Після перевірки натисніть «Провести накладну».</p>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={postAsPaid} onChange={event => setPostAsPaid(event.target.checked)} />
                    Позначити повністю оплаченою під час проведення
                  </label>
                  {receiptActionError && <p role="alert" style={{ color: 'var(--crm-danger)', marginBottom: 0 }}>{receiptActionError}</p>}
                </div>
              )}
              <p>Дата: {new Date(selected.date).toLocaleDateString('uk-UA')}</p>
              <p>Постачальник: {selected.supplierName || '—'}</p>
              <p>Статус оплати: <strong style={{ color: selected.status === 'Draft' ? 'var(--crm-text-muted)' : selected.isPaid ? 'var(--crm-success)' : 'var(--crm-warning)' }}>
                {selected.status === 'Draft'
                  ? selected.isPaid ? 'Буде позначено як оплачено після проведення' : 'Буде визначено після проведення'
                  : selected.isPaid ? 'Оплачено' : 'Не оплачено'}
              </strong></p>
              <p>Термін оплати: <strong>{new Date(selected.paymentDueDate || selected.date).toLocaleDateString('uk-UA')}</strong></p>
              <p>Умова оплати на момент приходу: <strong>{selected.supplierPaymentTermDays > 0 ? `${selected.supplierPaymentTermDays} календарних днів` : 'оплата в день накладної'}</strong></p>
              <p>
                Сплачено: <strong style={{ color: 'var(--crm-success)' }}>{selected.paidAmount.toFixed(2)} ₴</strong>
                {' · '}Залишок: <strong style={{ color: selected.outstandingAmount > 0 ? 'var(--crm-danger)' : 'var(--crm-success)' }}>{selected.outstandingAmount.toFixed(2)} ₴</strong>
              </p>
              {canManagePayments && selected.status !== 'Draft' && selected.status !== 'Cancelled' && selected.outstandingAmount > 0 && (
                <div className="no-print" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end', padding: 12, background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', marginBottom: 12 }}>
                  <label>Сума<input value={paymentAmount} onChange={event => setPaymentAmount(event.target.value)} placeholder={selected.outstandingAmount.toFixed(2)} style={{ display: 'block', padding: 6, width: 130 }} /></label>
                  <label>Дата<input type="date" value={paymentDate} onChange={event => setPaymentDate(event.target.value)} style={{ display: 'block', padding: 6 }} /></label>
                  <label>Спосіб<input value={paymentMethod} onChange={event => setPaymentMethod(event.target.value)} placeholder="Банк / готівка" style={{ display: 'block', padding: 6 }} /></label>
                  <label>Референс<input value={paymentReference} onChange={event => setPaymentReference(event.target.value)} placeholder="№ транзакції" style={{ display: 'block', padding: 6 }} /></label>
                  <label>Примітка<input value={paymentNotes} onChange={event => setPaymentNotes(event.target.value)} style={{ display: 'block', padding: 6 }} /></label>
                  <button type="button" onClick={addPayment} style={btn('success')}>Додати платіж</button>
                  {paymentError && <div style={{ flexBasis: '100%', color: 'var(--crm-danger)', fontSize: 13 }}>{paymentError}</div>}
                </div>
              )}
              {(selected.payments?.length ?? 0) > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <h4>Журнал платежів</h4>
                  <div className="inventory-receipts-detail-table-scroll">
                  <table className="inventory-receipts-detail-payments-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={thStyle}>Дата</th><th style={thStyle}>Сума</th><th style={thStyle}>Спосіб / референс</th><th style={thStyle}>Автор</th><th className="no-print" style={thStyle}></th></tr></thead>
                    <tbody>{selected.payments!.map(payment => (
                      <tr key={payment.id}>
                        <td style={tdStyle}>{new Date(payment.paymentDate).toLocaleDateString('uk-UA')}{payment.isReversed ? ' · Сторновано' : ''}</td>
                        <td style={{ ...tdStyle, textDecoration: payment.isReversed ? 'line-through' : 'none' }}>{payment.amount.toFixed(2)} ₴</td>
                        <td style={tdStyle}>{[payment.batchNumber ? `Реєстр ${payment.batchNumber}` : '', payment.method, payment.reference, payment.notes].filter(Boolean).join(' · ') || '—'}</td>
                        <td style={tdStyle}>{payment.createdByUsername || 'Система'}{payment.isReversed ? ` · сторнував ${payment.reversedByUsername || '—'}${payment.reversalReason ? `: ${payment.reversalReason}` : ''}` : ''}</td>
                        <td className="no-print" style={tdStyle}>{canManagePayments && selected.status !== 'Cancelled' && !payment.isReversed && <button type="button" onClick={() => reversePayment(payment.id)} style={btn('danger', { padding: '4px 8px' })}>Сторнувати</button>}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                  </div>
                </div>
              )}
              {selected.notes && <p>Примітки: {selected.notes}</p>}
              <div className="inventory-receipts-detail-table-scroll">
              <table className="inventory-receipts-detail-items-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Продукт</th>
                    <th style={thStyle}>Категорія</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th>
                    <th style={thStyle}>Од.</th>
                    <th style={thStyle}>Упакування</th>
                    <th style={thStyle}>Партія / придатність</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Ціна</th>
                    {showVatColumn && <th style={{ ...thStyle, textAlign: 'right' }}>ПДВ</th>}
                    <th style={{ ...thStyle, textAlign: 'right' }}>Сума</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.items || []).map((i: InventoryReceiptItem) => (
                    <tr key={i.id}>
                    <td style={tdStyle}>{i.productName || '—'}</td>
                    <td style={tdStyle}>{i.categoryName || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{i.quantity.toFixed(2)}</td>
                      <td style={tdStyle}>{getWeightTypeLabel(i.weightType)}</td>
                      <td style={tdStyle}>
                        {[
                          i.packageCount && i.packageCount > 0 ? `${i.packageCount} шт` : '',
                          i.packageWeight && i.packageWeight > 0 ? `${i.packageWeight} ${getWeightTypeLabel(i.weightType)}/шт` : '',
                          i.packagePrice && i.packagePrice > 0 ? `${i.packagePrice.toFixed(2)} ₴/шт` : '',
                        ].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td style={tdStyle}>
                        <div>{i.lotNumber || 'Без номера'}</div>
                        <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>
                          {i.manufactureDate ? `вир. ${new Date(i.manufactureDate).toLocaleDateString('uk-UA')}` : ''}
                          {i.expirationDate ? ` · до ${new Date(i.expirationDate).toLocaleDateString('uk-UA')}` : ''}
                        </div>
                        <div style={{ fontSize: 11, color: i.remainingQuantity > 0 ? 'var(--crm-success)' : 'var(--crm-text-muted)' }}>
                          залишок партії: {i.remainingQuantity.toFixed(2)} {getWeightTypeLabel(i.weightType)}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{i.unitPrice.toFixed(2)}</td>
                      {showVatColumn && <td style={{ ...tdStyle, textAlign: 'right' }}>{calculateReceiptVatAmount(i.quantity * i.unitPrice, selectedVatRate).toFixed(2)}</td>}
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{(i.quantity * i.unitPrice).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {showVatColumn && (
                    <>
                      <tr>
                        <td colSpan={8} style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>Разом без ПДВ:</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{selected.totalAmount.toFixed(2)} ₴</td>
                      </tr>
                      <tr>
                        <td colSpan={8} style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>ПДВ ({selectedVatRate}%):</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{selectedVatAmount.toFixed(2)} ₴</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td colSpan={showVatColumn ? 8 : 7} style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{showVatColumn ? 'Разом з ПДВ:' : 'Разом:'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{showVatColumn ? (selected.totalAmount + selectedVatAmount).toFixed(2) : selected.totalAmount.toFixed(2)} ₴</td>
                  </tr>
                </tfoot>
              </table>
              </div>
                  </>
                );
              })()}
            </div>
          )}

          {!selected && !showForm && !importPreview && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Оберіть накладну зі списку або створіть нову.</div>
          )}
        </div>
      </div>

      {ocrOpen && (
        <div className="inventory-receipt-import-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1100, overflowY: 'auto', padding: '24px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
          <div className="inventory-receipt-import-modal" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', width: '100%', maxWidth: 1280, borderRadius: 'var(--crm-radius-lg)', boxShadow: 'var(--crm-shadow-sm)', marginBottom: 24 }}>
            <div className="inventory-receipt-import-header" style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', borderRadius: 'var(--crm-radius-lg) var(--crm-radius-lg) 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0 }}>Імпорт накладної з фото</h3>
                <div style={{ marginTop: 3, fontSize: 12, opacity: .9 }}>Фото безпечно надсилається на сервер і розпізнається сервісом Google Document AI (єдиний разовий OCR-запит, вміст фото в браузері не зберігається)</div>
              </div>
              <button type="button" disabled={ocrBusy} onClick={() => setOcrOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--crm-primary-contrast)', fontSize: 24, cursor: ocrBusy ? 'wait' : 'pointer' }}>×</button>
            </div>

            <div className="inventory-receipt-import-content" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <button type="button" onClick={() => photoRef.current?.click()} disabled={ocrBusy} style={btn(ocrBusy ? 'muted' : 'success')}>
                  📷 Обрати фото або відкрити камеру
                </button>
                <span style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>Найкращий результат: рівне фото зверху, без тіней, текст займає весь кадр.</span>
              </div>

              {(ocrBusy || ocrStatus) && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, marginBottom: 5 }}>{ocrStatus || 'Обробка...'}</div>
                  <div style={{ height: 8, background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ width: ocrBusy ? '100%' : `${ocrProgress}%`, height: '100%', background: 'var(--crm-primary)', transition: 'width .2s', opacity: ocrBusy ? 0.6 : 1 }} />
                  </div>
                </div>
              )}

              {ocrError && <div style={{ padding: '10px 12px', marginBottom: 12, border: '1px solid var(--crm-danger)', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)' }}>{ocrError}</div>}

              {ocrText && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontWeight: 700, marginBottom: 5 }}>Розпізнаний текст — можна виправити вручну</label>
                  <textarea
                    value={ocrText}
                    onChange={(e) => setOcrText(e.target.value)}
                    rows={10}
                    style={{ width: '100%', boxSizing: 'border-box', padding: 10, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)', fontFamily: 'monospace', fontSize: 12 }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button type="button" onClick={analyzeOcrText} disabled={ocrBusy} style={btn(ocrBusy ? 'muted' : 'accent')}>Проаналізувати текст</button>
                  </div>
                </div>
              )}

              {ocrPreview && (
                <>
                  <div className="crm-mobile-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
                    <label>Дата
                      <input type="date" max={maxReceiptDate} value={ocrPreview.date} onChange={(e) => setOcrPreview({ ...ocrPreview, date: e.target.value })} style={{ display: 'block', width: '100%', padding: 7, boxSizing: 'border-box' }} />
                    </label>
                    <label>Постачальник
                      <SearchSelect
                        items={suppliers.map((supplier) => ({ id: String(supplier.id), name: supplier.name }))}
                        value={ocrPreview.supplierId ? String(ocrPreview.supplierId) : ''}
                        onChange={(id) => {
                          const supplier = suppliers.find((item) => String(item.id) === id);
                          setOcrPreview({ ...ocrPreview, supplierId: supplier?.id ?? null, supplierName: supplier?.name ?? '', supplierConfidence: supplier ? 1 : 0 });
                        }}
                        placeholder="🔍 Пошук постачальника..."
                      />
                    </label>
                    <label>№ накладної
                      <input value={ocrPreview.invoiceNumber} onChange={(e) => setOcrPreview({ ...ocrPreview, invoiceNumber: e.target.value })} style={{ display: 'block', width: '100%', padding: 7, boxSizing: 'border-box' }} />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, paddingTop: 22 }}>
                      <input type="checkbox" checked={ocrPreview.isPaid} onChange={(e) => setOcrPreview({ ...ocrPreview, isPaid: e.target.checked })} />
                      Після проведення позначити як оплачену
                    </label>
                  </div>

                  <div className="inventory-receipt-import-scroll" style={{ overflowX: 'auto', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)' }}>
                    <table className="inventory-receipt-import-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
                      <thead><tr>
                        <th style={thStyle}>Рядок</th>
                        <th style={thStyle}>Текст із фото</th>
                        <th style={{ ...thStyle, minWidth: 280 }}>Продукт CRM *</th>
                        <th style={thStyle}>Збіг</th>
                        <th style={thStyle}>Ціна</th>
                        <th style={thStyle}>Сума</th>
                        <th style={thStyle}>Од.</th>
                        <th style={thStyle}></th>
                      </tr></thead>
                      <tbody>
                        {ocrPreview.rows.map((row) => {
                          const needsReview = !row.productId || row.matchConfidence < .72;
                          return (
                            <tr key={row.lineNumber} style={{ background: row.excluded ? 'var(--crm-surface-alt)' : needsReview ? 'var(--crm-warning-bg)' : undefined, opacity: row.excluded ? .6 : 1 }}>
                              <td data-label="Рядок" style={tdStyle}>{row.lineNumber}</td>
                              <td data-label="Текст із фото" style={{ ...tdStyle, maxWidth: 270, fontSize: 12 }}>{row.sourceText}</td>
                              <td data-label="Продукт CRM" style={tdStyle}>
                                <ItemQuantityField
                                  items={products.filter((product) => product.isActive !== false).map((product) => ({ id: String(product.id), name: product.name }))}
                                  value={row.productId ? String(row.productId) : ''}
                                  onChange={id => {
                                    const product = products.find((item) => String(item.id) === id);
                                    updateOcrRow(row.lineNumber, {
                                      productId: product?.id ?? null,
                                      productName: product?.name ?? '',
                                      weightType: product?.weightType ?? row.weightType,
                                      matchConfidence: product ? 1 : 0,
                                      matchMethod: product ? 'Обрано вручну' : 'Не знайдено',
                                      warning: '',
                                    });
                                  }}
                                  quantity={String(row.quantity)}
                                  onQuantityChange={value => {
                                    const quantity = parseFloat(value) || 0;
                                    updateOcrRow(row.lineNumber, { quantity, lineTotal: quantity * row.unitPrice });
                                  }}
                                  label="Продукт"
                                  quantityLabel="Кількість"
                                  unit={WEIGHT_TYPES.find(type => type.value === row.weightType)?.label ?? row.weightType}
                                  min={0}
                                  step={0.001}
                                  quantityRequired={false}
                                  placeholder="🔍 Пошук продукту..."
                                />
                              </td>
                              <td data-label="Збіг" style={tdStyle}>
                                <span style={{ color: needsReview ? 'var(--crm-warning)' : 'var(--crm-success)', fontWeight: 600 }}>{row.matchMethod}</span>
                                <div style={{ fontSize: 11 }}>{Math.round(row.matchConfidence * 100)}%</div>
                                {row.warning && <div style={{ color: 'var(--crm-danger)', fontSize: 11, marginTop: 3 }}>{row.warning}</div>}
                              </td>
                              <td data-label="Ціна" style={tdStyle}><input type="number" min="0" step="0.01" value={row.unitPrice} onChange={(e) => {
                                const unitPrice = parseFloat(e.target.value) || 0;
                                updateOcrRow(row.lineNumber, { unitPrice, lineTotal: row.quantity * unitPrice });
                              }} style={{ width: 95, padding: 5 }} /></td>
                              <td data-label="Сума" style={tdStyle}><input type="number" min="0" step="0.01" value={row.lineTotal} onChange={(e) => {
                                const lineTotal = parseFloat(e.target.value) || 0;
                                updateOcrRow(row.lineNumber, { lineTotal, unitPrice: row.quantity > 0 ? lineTotal / row.quantity : row.unitPrice });
                              }} style={{ width: 100, padding: 5 }} /></td>
                              <td data-label="Одиниця" style={tdStyle}>
                                <select value={row.weightType} onChange={(e) => updateOcrRow(row.lineNumber, { weightType: e.target.value })} style={{ padding: 5 }}>
                                  {WEIGHT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                                </select>
                              </td>
                              <td data-label="Дія" style={tdStyle}>
                                <button
                                  type="button"
                                  onClick={() => updateOcrRow(row.lineNumber, { excluded: !row.excluded })}
                                  style={btn(row.excluded ? 'success' : 'danger', { padding: '5px 8px', whiteSpace: 'nowrap' })}
                                >
                                  {row.excluded ? 'Повернути' : 'Пропустити'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {ocrPreview.unparsedLines.length > 0 && (
                    <details style={{ marginTop: 12, padding: 10, background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-sm)' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Нерозібрані рядки ({ocrPreview.unparsedLines.length})</summary>
                      <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{ocrPreview.unparsedLines.join('\n')}</pre>
                    </details>
                  )}

                  <div className="inventory-receipt-import-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                    <button type="button" onClick={() => setOcrOpen(false)} style={btn('muted')}>Скасувати</button>
                    <button type="button" onClick={applyOcrPreview} style={btn('success')}>Перенести у форму накладної</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {batchOpen && (
        <div className="inventory-receipt-import-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1100, overflowY: 'auto', padding: '24px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
          <div className="inventory-receipt-import-modal" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', width: '100%', maxWidth: 1400, borderRadius: 'var(--crm-radius-lg)', boxShadow: 'var(--crm-shadow-sm)', marginBottom: 24 }}>
            <div className="inventory-receipt-import-header" style={{ background: 'var(--crm-danger)', color: 'var(--crm-primary-contrast)', borderRadius: 'var(--crm-radius-lg) var(--crm-radius-lg) 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0 }}>Імпорт кількох накладних з Excel</h3>
                <div style={{ marginTop: 3, fontSize: 12, opacity: .9 }}>Заповніть шаблон даними, розпізнаними AI з фото накладних, і завантажте файл</div>
              </div>
              <button type="button" disabled={batchBusy || batchSubmitting} onClick={() => setBatchOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--crm-primary-contrast)', fontSize: 24, cursor: (batchBusy || batchSubmitting) ? 'wait' : 'pointer' }}>×</button>
            </div>

            <div className="inventory-receipt-import-content" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <InventoryWarehouseSelect value={batchWarehouseId} onChange={setBatchWarehouseId} disabled={batchBusy || batchSubmitting} />
                <button type="button" onClick={downloadBatchTemplate} style={btn('accent')}>⬇️ Завантажити шаблон</button>
                <button type="button" onClick={() => batchFileRef.current?.click()} disabled={batchBusy} style={btn(batchBusy ? 'muted' : 'success')}>📁 Обрати файл (.xlsx/.xlsm)</button>
                <span style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>Один файл може містити кілька накладних від різних постачальників.</span>
              </div>

              {batchBusy && <div style={{ marginBottom: 12, fontSize: 13 }}>Завантаження та аналіз файлу...</div>}
              {batchError && <div style={{ padding: '10px 12px', marginBottom: 12, border: '1px solid var(--crm-danger)', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', whiteSpace: 'pre-wrap' }}>{batchError}</div>}
              {batchResultMessage && <div style={{ padding: '10px 12px', marginBottom: 12, border: '1px solid var(--crm-success)', background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderRadius: 'var(--crm-radius-sm)' }}>{batchResultMessage}</div>}

              {batchFileWarnings.length > 0 && (
                <details style={{ marginBottom: 12, padding: 10, background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-sm)' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Попередження файлу ({batchFileWarnings.length})</summary>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                    {batchFileWarnings.map((warning, i) => <li key={i} style={{ fontSize: 12 }}>{warning}</li>)}
                  </ul>
                </details>
              )}

              {batchReceipts && batchReceipts.map((receipt, ri) => {
                const includedRows = receipt.rows.filter((row) => !row.excluded);
                const receiptValid = isBatchReceiptValid(receipt);
                const receiptTotal = includedRows.reduce((sum, row) => sum + row.lineTotal, 0);
                return (
                  <div key={ri} style={{
                    border: `1px solid ${receipt.excluded ? 'var(--crm-border)' : receiptValid ? 'var(--crm-success)' : 'var(--crm-danger)'}`,
                    background: receipt.excluded ? 'var(--crm-surface-alt)' : receiptValid ? 'var(--crm-surface)' : 'var(--crm-danger-bg)',
                    opacity: receipt.excluded ? .6 : 1,
                    borderRadius: 8,
                    padding: 14,
                    marginBottom: 14,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <strong style={{ fontSize: 15 }}>Накладна «{receipt.groupKey}»{!receiptValid && !receipt.excluded && <span style={{ color: 'var(--crm-danger)', marginLeft: 8, fontSize: 12 }}>(потребує виправлення)</span>}</strong>
                      <button
                        type="button"
                        onClick={() => updateBatchReceipt(ri, { excluded: !receipt.excluded })}
                        style={btn(receipt.excluded ? 'success' : 'danger', { padding: '5px 10px' })}
                      >
                        {receipt.excluded ? 'Повернути накладну' : 'Виключити накладну'}
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10, marginBottom: 10 }}>
                      <label>Дата
                        <input type="date" max={maxReceiptDate} value={receipt.date} onChange={(e) => updateBatchReceipt(ri, { date: e.target.value })} style={{ display: 'block', width: '100%', padding: 7, boxSizing: 'border-box' }} />
                      </label>
                      <label>Постачальник
                        <SearchSelect
                          items={suppliers.map((supplier) => ({ id: String(supplier.id), name: supplier.name }))}
                          value={receipt.supplierId ? String(receipt.supplierId) : ''}
                          onChange={(id) => {
                            const supplier = suppliers.find((item) => String(item.id) === id);
                            updateBatchReceipt(ri, { supplierId: supplier?.id ?? null, supplierName: supplier?.name ?? '', supplierConfidence: supplier ? 1 : 0, supplierMatchMethod: supplier ? 'Обрано вручну' : '' });
                          }}
                          placeholder="🔍 Пошук постачальника..."
                        />
                      </label>
                      <label>№ накладної
                        <input value={receipt.invoiceNumber} onChange={(e) => updateBatchReceipt(ri, { invoiceNumber: e.target.value })} style={{ display: 'block', width: '100%', padding: 7, boxSizing: 'border-box' }} />
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 7, paddingTop: 22 }}>
                        <input type="checkbox" checked={receipt.isPaid} onChange={(e) => updateBatchReceipt(ri, { isPaid: e.target.checked })} />
                        Після проведення позначити як оплачену
                      </label>
                      <label>Примітки
                        <input value={receipt.notes} onChange={(e) => updateBatchReceipt(ri, { notes: e.target.value })} style={{ display: 'block', width: '100%', padding: 7, boxSizing: 'border-box' }} />
                      </label>
                    </div>

                    {receipt.errors.length > 0 && <div style={{ color: 'var(--crm-danger)', fontSize: 12, marginBottom: 8 }}>{receipt.errors.join(' ')}</div>}
                    {receipt.warnings.length > 0 && <div style={{ color: 'var(--crm-warning)', fontSize: 12, marginBottom: 8 }}>{receipt.warnings.join(' ')}</div>}

                    <div className="inventory-receipt-import-scroll" style={{ overflowX: 'auto', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)' }}>
                      <table className="inventory-receipt-import-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 950 }}>
                        <thead><tr>
                          <th style={thStyle}>Рядок</th>
                          <th style={{ ...thStyle, minWidth: 260 }}>Товар CRM *</th>
                          <th style={thStyle}>Збіг</th>
                          <th style={thStyle}>К-сть шт</th>
                          <th style={thStyle}>Вага 1 шт</th>
                          <th style={thStyle}>Ціна/шт</th>
                          <th style={thStyle}>Ціна за од.</th>
                          <th style={thStyle}>Од.</th>
                          <th style={thStyle}>Сума</th>
                          <th style={thStyle}></th>
                        </tr></thead>
                        <tbody>
                          {receipt.rows.map((row, rowi) => {
                            const rowNeedsReview = !row.productId || row.errors.length > 0;
                            return (
                              <tr key={row.rowNumber} style={{ background: row.excluded ? 'var(--crm-surface-alt)' : rowNeedsReview ? 'var(--crm-warning-bg)' : undefined, opacity: row.excluded ? .6 : 1 }}>
                                <td data-label="Рядок" style={tdStyle}>{row.rowNumber}</td>
                                <td data-label="Товар CRM" style={tdStyle}>
                                  <ItemQuantityField
                                    items={products.filter((product) => product.isActive !== false).map((product) => ({ id: String(product.id), name: product.name }))}
                                    value={row.productId ? String(row.productId) : ''}
                                    onChange={id => {
                                      const product = products.find((item) => String(item.id) === id);
                                      updateBatchRow(ri, rowi, {
                                        productId: product?.id ?? null,
                                        productName: product?.name ?? '',
                                        weightType: product?.weightType ?? row.weightType,
                                        productConfidence: product ? 1 : 0,
                                        productMatchMethod: product ? 'Обрано вручну' : '',
                                        errors: product ? row.errors.filter((e) => !e.includes('товар')) : row.errors,
                                      });
                                    }}
                                    quantity={String(row.quantity)}
                                    onQuantityChange={value => updateBatchRow(ri, rowi, { quantity: parseFloat(value) || 0 })}
                                    label="Товар"
                                    quantityLabel="Кількість"
                                    unit={WEIGHT_TYPES.find(type => type.value === row.weightType)?.label ?? row.weightType}
                                    min={0}
                                    step={0.001}
                                    quantityDisabled={!!(row.packageCount && row.packageWeight)}
                                    quantityRequired={false}
                                    placeholder={row.sourceProductName || '🔍 Пошук продукту...'}
                                  />
                                  {row.sourceProductName && <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>з файлу: {row.sourceProductName}</div>}
                                </td>
                                <td data-label="Збіг" style={tdStyle}>
                                  <span style={{ color: rowNeedsReview ? 'var(--crm-warning)' : 'var(--crm-success)', fontWeight: 600 }}>{row.productMatchMethod || '—'}</span>
                                  <div style={{ fontSize: 11 }}>{Math.round(row.productConfidence * 100)}%</div>
                                  {row.errors.map((err, ei) => <div key={ei} style={{ color: 'var(--crm-danger)', fontSize: 11, marginTop: 2 }}>{err}</div>)}
                                  {row.warnings.map((warn, wi) => <div key={wi} style={{ color: 'var(--crm-warning)', fontSize: 11, marginTop: 2 }}>{warn}</div>)}
                                </td>
                                <td data-label="К-сть шт" style={tdStyle}><input type="number" min="0" step="0.001" value={row.packageCount ?? ''} onChange={(e) => updateBatchRow(ri, rowi, { packageCount: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })} style={{ width: 80, padding: 5 }} /></td>
                                <td data-label="Вага 1 шт" style={tdStyle}><input type="number" min="0" step="0.001" value={row.packageWeight ?? ''} onChange={(e) => updateBatchRow(ri, rowi, { packageWeight: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })} style={{ width: 80, padding: 5 }} /></td>
                                <td data-label="Ціна/шт" style={tdStyle}><input type="number" min="0" step="0.01" value={row.packagePrice ?? ''} onChange={(e) => updateBatchRow(ri, rowi, { packagePrice: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })} style={{ width: 85, padding: 5 }} /></td>
                                <td data-label="Ціна за од." style={tdStyle}><input type="number" min="0" step="0.01" value={row.unitPrice} disabled={!!(row.packagePrice && row.packageWeight)} onChange={(e) => updateBatchRow(ri, rowi, { unitPrice: parseFloat(e.target.value) || 0 })} style={{ width: 95, padding: 5, border: '1px solid var(--crm-border)', background: row.packagePrice && row.packageWeight ? 'var(--crm-surface-alt)' : 'var(--crm-input-bg)', color: 'var(--crm-text)' }} /></td>
                                <td data-label="Одиниця" style={tdStyle}>
                                  <select value={row.weightType} onChange={(e) => updateBatchRow(ri, rowi, { weightType: e.target.value })} style={{ padding: 5 }}>
                                    {WEIGHT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                                  </select>
                                </td>
                                <td data-label="Сума" style={tdStyle}>{row.lineTotal.toFixed(2)}</td>
                                <td data-label="Дія" style={tdStyle}>
                                  <button
                                    type="button"
                                    onClick={() => updateBatchRow(ri, rowi, { excluded: !row.excluded })}
                                    style={btn(row.excluded ? 'success' : 'danger', { padding: '5px 8px', whiteSpace: 'nowrap' })}
                                  >
                                    {row.excluded ? 'Повернути' : 'Пропустити'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 700 }}>Разом: {receiptTotal.toFixed(2)}</div>
                  </div>
                );
              })}

              {batchReceipts && (
                <div className="inventory-receipt-import-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <button type="button" onClick={() => setBatchOpen(false)} disabled={batchSubmitting} style={btn('muted')}>Скасувати</button>
                  <button
                    type="button"
                    onClick={submitBatch}
                    disabled={batchSubmitting || !batchWarehouseId || batchReceipts.filter((r) => !r.excluded).length === 0 || !batchReceipts.filter((r) => !r.excluded).every((r) => isBatchReceiptValid(r))}
                    style={btn(batchSubmitting ? 'muted' : 'success')}
                  >
                    {batchSubmitting ? 'Створення...' : `Створити накладні (${batchReceipts.filter((r) => !r.excluded).length})`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .inventory-receipts-toolbar,
        .inventory-receipts-form-grid,
        .inventory-receipts-form-actions,
        .inventory-receipts-detail-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .inventory-receipts-mobile-list-toggle {
          display: none;
        }

        .inventory-receipts-card,
        .inventory-receipts-form,
        .inventory-receipts-detail-header,
        .inventory-receipts-toolbar input,
        .inventory-receipts-form input,
        .inventory-receipts-form select,
        .inventory-receipts-detail-header button,
        .inventory-receipts-import-button {
          border-radius: 8px;
        }

        .inventory-receipts-form input:not([type="checkbox"]),
        .inventory-receipts-form select,
        .inventory-receipts-toolbar input {
          border: 1px solid var(--crm-border);
          padding: 6px 8px;
          background: var(--crm-input-bg);
          color: var(--crm-text);
        }

        .inventory-receipts-items-table {
          overflow-x: auto;
        }

        .inventory-receipts-entry-list {
          display: grid;
        }

        .inventory-receipts-entry-card {
          min-width: 0;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 55%);
          grid-template-areas:
            "product values"
            "details details";
          align-items: start;
          gap: 8px;
          padding: 8px;
          border: 1px solid var(--crm-border);
          border-radius: 10px;
          background: var(--crm-surface);
          color: var(--crm-text);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .inventory-receipts-product-field {
          grid-area: product;
          position: relative;
          z-index: 1;
          min-width: 0;
          align-self: center;
        }

        .inventory-receipts-product-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          min-width: 24px;
          height: 24px;
          padding: 0 5px;
          border-radius: 999px;
          background: var(--crm-surface-alt);
          color: var(--crm-text-muted);
          font-size: 11px;
          font-weight: 600;
        }

        .inventory-receipts-product-search-row {
          display: grid;
          grid-template-columns: 24px minmax(0, 1fr);
          align-items: center;
          gap: 4px;
          min-width: 0;
        }

        .inventory-receipts-product-controls {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 4px;
          min-width: 0;
        }

        .inventory-receipts-product-unit {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          color: var(--crm-text-muted);
          font-size: 9px;
          font-weight: 600;
        }

        .inventory-receipts-product-unit select {
          min-height: 30px;
          padding: 2px 5px;
          border: 1px solid var(--crm-border);
          border-radius: 999px;
          background: var(--crm-input-bg);
          color: var(--crm-text);
          font-size: 11px;
        }

        .inventory-receipts-remove-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          width: 30px;
          height: 30px;
          padding: 0;
          border: 0;
          border-radius: 999px;
          background: var(--crm-danger);
          color: var(--crm-primary-contrast);
          font-size: 21px;
          line-height: 1;
          cursor: pointer;
        }

        .inventory-receipts-remove-button:focus-visible {
          outline: 2px solid var(--crm-primary);
          outline-offset: 2px;
        }

        .inventory-receipts-entry-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 10px;
          margin-top: 10px;
        }

        .inventory-receipts-calculation-grid {
          grid-area: values;
          position: relative;
          z-index: 2;
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          align-items: center;
          gap: 8px;
          min-width: 0;
          margin: 0;
          align-self: center;
          width: 100%;
          max-width: 480px;
          justify-self: end;
        }

        .inventory-receipts-float-field {
          position: relative;
          display: block;
          min-width: 0;
          padding-top: 6px;
          color: var(--crm-text-muted);
          font-size: 11px;
          font-weight: 600;
        }

        .inventory-receipts-float-field > input,
        .inventory-receipts-float-field > select {
          min-width: 0;
        }

        .inventory-receipts-float-field > span {
          position: absolute;
          z-index: 1;
          top: -1px;
          left: 8px;
          max-width: calc(100% - 16px);
          overflow: hidden;
          padding: 0 4px;
          border-radius: 4px;
          background: var(--crm-surface);
          color: var(--crm-text-muted);
          font-size: 10px;
          line-height: 14px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .inventory-receipts-float-field input,
        .inventory-receipts-float-field select {
          box-sizing: border-box;
          width: 100%;
          min-width: 0;
          height: 34px;
          border: 1px solid var(--crm-border);
          border-radius: 7px;
          padding: 4px 6px;
          background: var(--crm-input-bg);
          color: var(--crm-text);
          font-size: 12px;
          font-weight: 500;
        }

        .inventory-receipts-float-field input:focus,
        .inventory-receipts-float-field select:focus {
          outline: 2px solid var(--crm-primary);
          outline-offset: 1px;
        }

        .inventory-receipts-line-total input {
          border-color: var(--crm-border);
          background: var(--crm-surface-alt);
          font-weight: 700;
        }

        .inventory-receipts-detail-table-scroll {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overscroll-behavior-x: contain;
          -webkit-overflow-scrolling: touch;
        }

        .inventory-receipts-detail {
          box-sizing: border-box;
          min-width: 0;
          max-width: 100%;
        }

        .inventory-receipts-entry-details {
          flex: 1 1 auto;
          min-width: 0;
          margin-top: 0;
        }

        .inventory-receipts-entry-details > summary {
          min-height: 32px;
          display: flex;
          align-items: center;
          font-size: 12px;
        }

        .inventory-receipts-entry-footer {
          grid-area: details;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          min-width: 0;
        }

        .inventory-receipts-entry-footer > .inventory-receipts-product-controls {
          align-self: flex-start;
        }

        .inventory-receipts-entry-details__actions {
          display: flex;
          align-items: center;
          flex: 0 0 auto;
        }

        .inventory-receipts-calc-input {
          font-variant-numeric: tabular-nums;
        }

        @media print {
          .no-print { display: none !important; }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .inventory-receipts-detail {
            --crm-surface: #fff;
            --crm-surface-alt: #f8f9fa;
            --crm-input-bg: #fff;
            --crm-primary: #eef4fa;
            --crm-navbar-dropdown-bg: #eef4fa;
            --crm-primary-contrast: #2c3e50;
            --crm-text: #2c3e50;
            --crm-text-muted: #666;
            --crm-table-text: #2c3e50;
            --crm-table-row-border: #dfe6e9;
            --crm-border: #dfe6e9;
            --crm-success: #196f3d;
            --crm-success-bg: #eafaf1;
            --crm-danger: #c0392b;
            --crm-danger-bg: #fdecea;
            --crm-warning: #9a6700;
            --crm-warning-bg: #fff8e8;
            background: #fff !important;
            color: #111 !important;
            border: 0 !important;
            box-shadow: none !important;
          }
          .inventory-receipts-detail-table-scroll { overflow: visible; }
        }

        @media (max-width: 768px) {
          .inventory-receipts-page {
            padding: 10px !important;
          }

          .inventory-receipts-page > h2 {
            margin: 0 0 8px !important;
            padding-bottom: 6px !important;
            font-size: 18px;
          }

          .inventory-receipts-page > .responsive-split-layout {
            gap: 10px;
            margin-top: 8px !important;
          }

          .inventory-receipts-page > .responsive-split-layout > div {
            flex: 1 1 auto !important;
            width: 100%;
          }

          .inventory-receipts-toolbar input,
          .inventory-receipts-toolbar button,
          .inventory-receipts-page > .responsive-split-layout > div > label select {
            min-height: 44px;
          }

          .inventory-receipts-detail {
            padding: 10px !important;
          }

          .inventory-receipts-detail-items-table {
            min-width: 850px;
          }

          .inventory-receipts-detail-payments-table {
            min-width: 620px;
          }

          .inventory-receipts-card {
            padding: 10px !important;
          }

          .inventory-receipts-card > h3 {
            margin: 0 0 8px;
            font-size: 16px;
          }

          .inventory-receipts-list-panel {
            max-height: 45vh !important;
          }

          .inventory-receipts-import-actions {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 6px !important;
            margin-bottom: 8px !important;
          }

          .inventory-receipts-import-actions .inventory-receipts-import-button:last-child {
            grid-column: 1 / -1;
          }

          .inventory-receipts-mobile-list-toggle {
            display: flex;
            width: 100%;
            min-height: 44px;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            margin-bottom: 6px;
            padding: 8px 10px;
            border: 1px solid var(--crm-border);
            border-radius: 9px;
            background: var(--crm-surface);
            color: var(--crm-text);
            font-size: 13px;
            font-weight: 600;
            text-align: left;
            cursor: pointer;
          }

          .inventory-receipts-mobile-list:not(.is-open) {
            display: none;
          }

          .inventory-receipts-entry-card {
            grid-template-columns: minmax(0, 1fr);
            grid-template-areas:
              "product"
              "values"
              "details";
            gap: 6px;
            padding: 7px;
            border-radius: 10px;
          }

          .inventory-receipts-product-search-row {
            grid-template-columns: 28px minmax(0, 1fr);
            gap: 4px;
          }

          .inventory-receipts-product-unit select {
            min-height: 40px;
            padding: 2px 6px;
            font-size: 11px;
          }

          .inventory-receipts-remove-button {
            width: 44px;
            height: 44px;
          }

          .inventory-receipts-calculation-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px 6px;
            max-width: none;
          }

          .inventory-receipts-float-field {
            padding-top: 5px;
          }

          .inventory-receipts-float-field input,
          .inventory-receipts-float-field select {
            min-height: 44px;
            height: 44px;
            padding-left: 7px;
            padding-right: 7px;
            font-size: 16px;
          }

          .inventory-receipts-float-field > span {
            font-size: 10px;
          }

          .inventory-receipts-items-table,
          .inventory-receipts-items-table tbody {
            display: block;
            width: 100%;
          }

          .inventory-receipts-items-table thead {
            display: none;
          }

          .inventory-receipts-items-table tbody tr {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 8px;
            padding: 8px;
            margin-bottom: 8px;
            border: 1px solid var(--crm-border);
            border-radius: 10px;
            background: var(--crm-surface);
            color: var(--crm-text);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          }

          .inventory-receipts-items-table tbody td {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            gap: 5px;
            width: auto !important;
            min-width: 0;
            padding: 0 !important;
            border: 0 !important;
            overflow: visible !important;
            text-align: left !important;
          }

          .inventory-receipts-items-table tbody td::before {
            content: attr(data-label);
            color: var(--crm-text-muted);
            font-size: 11px;
            font-weight: 600;
            line-height: 1.25;
          }

          .inventory-receipts-items-table tbody .receipt-item-product {
            grid-column: 1 / -1;
          }

          .inventory-receipts-items-table tbody .receipt-item-total {
            grid-column: 1;
          }

          .inventory-receipts-items-table tbody .receipt-item-remove {
            grid-column: 2;
            align-items: flex-end;
          }

          .inventory-receipts-items-table tbody .receipt-item-product > div:last-child {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
            gap: 8px !important;
          }

          .inventory-receipts-items-table tbody .receipt-item-lot {
            grid-column: 1 / -1;
          }

          .inventory-receipts-items-table tbody .receipt-item-manufacture-date,
          .inventory-receipts-items-table tbody .receipt-item-expiration-date {
            min-width: 0;
          }

          .inventory-receipts-items-table tbody input:not([type="checkbox"]),
          .inventory-receipts-items-table tbody select {
            box-sizing: border-box;
            width: 100%;
            min-width: 0;
            min-height: 44px;
            padding: 7px 8px !important;
            border: 1px solid var(--crm-border) !important;
            border-radius: 8px !important;
            font-size: 16px !important;
          }

          .inventory-receipts-items-table tbody .receipt-item-remove button {
            min-height: 44px;
            min-width: 52px;
            border-radius: 8px;
          }

          .inventory-receipts-toolbar input {
            min-width: 0;
          }

          .inventory-receipts-toolbar button,
          .inventory-receipts-form-actions button,
          .inventory-receipts-detail-actions button,
          .inventory-receipts-form-button,
          .inventory-receipts-import-button {
            min-height: 44px;
            padding: 5px 7px !important;
            font-size: 11px !important;
            line-height: 1.2;
          }

          .inventory-receipts-form-grid > label,
          .inventory-receipts-detail-header {
            width: 100%;
          }

          .inventory-receipts-form-grid > label {
            flex-basis: 100% !important;
            min-width: 0 !important;
          }

          .inventory-receipts-form-grid {
            gap: 7px !important;
            margin-bottom: 8px !important;
          }

          .inventory-receipts-detail-header {
            align-items: flex-start !important;
            gap: 7px;
          }

          .inventory-receipts-detail-actions {
            width: 100%;
          }

          .inventory-receipts-form-grid > label input,
          .inventory-receipts-form-grid > label select {
            width: 100%;
            min-width: 0;
            min-height: 44px;
          }

          .inventory-receipts-entry-details {
            margin-top: 4px;
          }

          .inventory-receipts-entry-details summary {
            min-height: 44px;
          }

          .inventory-receipts-entry-footer {
            gap: 4px;
          }

          .inventory-receipts-entry-footer > .inventory-receipts-product-controls {
            min-height: 44px;
          }

          .inventory-receipts-entry-details > summary {
            font-size: 11px;
          }

          .inventory-receipts-entry-grid input {
            box-sizing: border-box;
            width: 100%;
            min-width: 0;
            min-height: 44px;
            font-size: 16px;
          }

          .inventory-receipts-page .inventory-receipts-form-button {
            min-height: 44px;
          }
        }

      `}</style>
    </div>
  );
};

export default InventoryReceiptsPage;
